<template>
	<div class="chat-box" @click="onClickChatBox($event)" @mousemove="readedMessage()">
		<div class="chat-container">
			<div class="chat-main-area">
				<div class="chat-header-stack">
					<chat-header
						:title="title"
						:subtitle="headerSubtitle"
						:is-dnd="chatIsDnd"
						:show-divider="!showHeaderBannerStack"
						:electron-mode="configStore.electronMode"
						:is-private="isPrivate"
						:mobile-mode="configStore.isMobile"
						:show-back="configStore.isMobile"
						@back="$emit('back')"
						@toggle-side="onClickMore"
						@private-video="showPrivateVideo"
						@group-video="onGroupVideo"
						@show-history="showHistoryBox"
					></chat-header>
					<div v-if="showHeaderBannerStack" class="chat-header-banner-stack">
						<chat-group-rtc-banner
							v-if="showHeaderRtcBanner"
							:rtcInfo="group.rtcInfo"
							:groupId="group.id"
							@join="onGroupVideo"
						></chat-group-rtc-banner>
						<chat-top-message
							v-if="showHeaderTopBanner"
							:group="group"
							:groupMembers="groupMembers"
							:msgInfo="latestTopMessage"
							:headImage="headImage(latestTopMessage)"
							:showName="showName(latestTopMessage)"
							:topCount="topMessageCount"
							@locate="locateMessage"
							@sync-top-state="onSyncTopMessageState"
							@view-all="openTopMessageList"
						></chat-top-message>
					</div>
					<div v-if="isGroup && groupInfoError" class="group-info-error" role="status" aria-live="polite">
						<i class="el-icon-warning-outline"></i>
						<span>{{ groupInfoErrorMessage }}</span>
						<el-button type="text" icon="el-icon-refresh" :loading="groupInfoLoading" @click="retryGroupInfo">
							重试
						</el-button>
					</div>
				</div>
				<div class="chat-body">
				<div class="content-box" :class="{ 'side-open': showSide }">
						<div class="message-stage">
						<message-viewport
							ref="messageViewport"
							:active-message-idx="activeMessageIdx"
							:bottom-status-type="bottomWindowStatusType"
							:group="group"
							:group-member-map="groupMemberMap"
							:group-members="groupMembers"
							:head-image="headImage"
							:history-status-type="historyStatusType"
							:is-allow-select-message="isAllowSelectMessage"
							:is-message-selected="isMessageSelected"
							:is-multi-select-mode="isMultiSelectMode"
							:mine-id="mine.id"
							:show-messages="showMessages"
							:show-min-idx="showMinIdx"
							:show-name="showName"
							@scroll="onScroll"
							@retry-history="retryLoadOlderHistory"
							@message-click="onMessageClick"
							@toggle-message-select="toggleMessageSelect"
							@call="onCall"
							@locate-quote="onLocateQuoteMessage"
							@copy="onCopyMessage"
							@resend="onResendMessage"
							@delete="onDeleteMessage"
							@recall="onRecallMessage"
							@quote="onQuoteMessage"
							@top="onTopMessage"
							@forward="onForwardMessage"
							@multi-select="onMultiSelectMessage"
							@add-sticker="onAddToSticker"
							@download="onDownloadFile"
							@edit="onEditMessage"
						></message-viewport>
						<div v-if="!isMultiSelectMode && (chat.atMe || chat.atAll)" class="locate-tip mention-tip" @click="scrollToAtMessage">
							<span class="tip-badge">@</span>
							<span class="tip-label">{{ headerMentionText || "有人@我" }}</span>
						</div>
						<div
							v-else-if="!isMultiSelectMode && !isInBottom"
							class="locate-tip scroll-tip"
							:title="newMessageSize > 0 ? '查看新消息' : '回到底部'"
							@click="scrollToBottom"
						>
							<span v-if="newMessageSize > 0" class="tip-badge">{{ newMessageSize > 99 ? "99+" : newMessageSize }}</span>
							<i class="el-icon-bottom"></i>
						</div>
						</div>
						<selection-toolbar
							v-if="isMultiSelectMode"
							:selected-count="selectedMessages.length"
							@batch-forward="onBatchForward"
							@merge-forward="onMergeForward"
							@batch-delete="onBatchDelete"
							@cancel="exitMultiSelectMode"
						></selection-toolbar>
						<chat-footer
							v-else
							ref="chatFooter"
							:before-check="checkSendRateLimit"
							:group="group"
							:group-members="groupMembers"
							:is-mobile="configStore.isMobile"
							:is-group="isGroup"
							:is-private="isPrivate"
							:is-receipt="isReceipt"
							:placeholder="placeholder"
							:quote-message="quoteMessage"
							:quote-show-name="showName(quoteMessage)"
							:not-allow-input-tip="notAllowInputTip"
							:chat-image-max-size="chatImageMaxSize"
							:chat-file-max-size="chatFileMaxSize"
							:chat-video-max-size="chatVideoMaxSize"
							:chat-audio-max-size="chatAudioMaxSize"
							:electron-mode="configStore.electronMode"
							@show-emotion="showEmotionBox"
							@screenshot="onScreenshot"
							@show-screenshot-menu="onShowScreenshotMenu"
							@toggle-receipt="onSwitchReceipt"
							@show-record="showRecordBox"
							@private-video="showPrivateVideo"
							@group-video="onGroupVideo"
							@show-history="showHistoryBox"
							@submit="sendMessage"
							@clear-quote="onQuoteMessage(null)"
							@send="notifySend"
							@image-before="onImageBefore"
							@image-success="onImageSuccess"
							@image-fail="onImageFail"
							@file-before="onFileBefore"
							@file-success="onFileSuccess"
							@file-fail="onFileFail"
							@video-before="onVideoBefore"
							@video-success="onVideoSuccess"
							@video-fail="onVideoFail"
						></chat-footer>
					</div>
				</div>
			</div>
			<div v-if="!configStore.isMobile" class="chat-side-box" :class="{ 'side-open': showSide }" @click.stop>
				<chat-group-side v-if="showSide && isGroup" :chat="chat" :groupId="activeGroupId" :groupMembers="groupMembers"
					@reload="loadGroup(activeGroupId)" @close="showSide = false"></chat-group-side>
				<chat-private-side v-if="showSide && isPrivate" :chat="chat" :userInfo="userInfo" @close="showSide = false"></chat-private-side>
			</div>
		</div>
		<div v-if="configStore.isMobile && showSide" class="chat-mobile-side-overlay">
			<button
				type="button"
				class="chat-mobile-side-mask"
				@click.stop="showSide = false"
			></button>
			<div class="chat-mobile-side-drawer">
				<chat-group-side v-if="isGroup" :chat="chat" :groupId="activeGroupId" :groupMembers="groupMembers"
					@reload="loadGroup(activeGroupId)" @close="showSide = false"></chat-group-side>
				<chat-private-side v-if="isPrivate" :chat="chat" :userInfo="userInfo" @close="showSide = false"></chat-private-side>
			</div>
		</div>
		<chat-overlays
			ref="chatOverlays"
			:chat="chat"
			:friend="friend"
			:group="group"
			:group-members="groupMembers"
			:head-image="headImage"
			:is-group="isGroup"
			:show-name="showName"
			:show-record="showRecord"
			:show-top-message-dialog="showTopMessageDialog"
			:top-message-list="topMessageList"
			:top-message-loading="topMessageLoading"
			:can-manage-top-messages="canManageTopMessages"
			:top-message-removing-id="removingTopMessageId"
			@update:show-top-message-dialog="showTopMessageDialog = $event"
			@emotion="onEmotion"
			@close-record="closeRecordBox"
			@send-record="onSendRecord"
			@invite-ok="onInviteOk"
			@locate-quote="locateMessage"
			@select-menu="onSelectMenu"
			@locate-top-message="onLocateTopMessage"
			@remove-top-message="onRemoveTopMessageFromDialog"
		></chat-overlays>
	</div>
</template>

<script>
import ChatGroupSide from "./ChatGroupSide.vue";
import ChatPrivateSide from "./ChatPrivateSide.vue";
import ChatHeader from "./ChatHeader.vue";
import MessageViewport from "./MessageViewport.vue";
import ChatOverlays from "./ChatOverlays.vue";
import SelectionToolbar from "./SelectionToolbar.vue";
import ChatFooter from "./ChatFooter.vue";
import ChatTopMessage from "./ChatTopMessage.vue";
import ChatGroupRtcBanner from "./ChatGroupRtcBanner.vue";
import configStore from "../../store/configStore";
import { uploadProtectedAsset } from "../../api/protectedAssetUpload";
import { dismissStack, resolveZIndex } from "../../utils/dismissStack";
import { isVideoFile, saveMediaClipboardEntry } from "../../utils/mediaClipboard";
import {
	getTopMessageExpectation,
	normalizeTopMessages,
	removeTopMessages,
	resolveTopMessageSyncState
} from "../../utils/topMessageState.js";
import { ensureStableMessageLocalId } from "../../utils/localMessageId.js";
import {
	isUnavailableGroup,
	isUnavailableGroupError,
	resolveUnavailableGroupNotice
} from "../../utils/groupAvailability.js";
import {
	extractForwardErrorMessage,
	normalizeForwardMessages,
	prepareForwardTargets
} from "../../utils/messageForward.js";

const GROUP_HISTORY_PAGE_SIZE = 30;
const TOP_HISTORY_LOAD_THRESHOLD = 80;
const HISTORY_LOADING_SHOW_DELAY_MS = 120;
const HISTORY_LOADING_MIN_VISIBLE_MS = 120;
const BOTTOM_WINDOW_LOADING_MIN_VISIBLE_MS = 180;
const MESSAGE_INITIAL_RENDER_SIZE = GROUP_HISTORY_PAGE_SIZE;
const MESSAGE_RENDER_BATCH_SIZE = GROUP_HISTORY_PAGE_SIZE;
const MESSAGE_RENDER_WINDOW_SIZE = GROUP_HISTORY_PAGE_SIZE * 10;
const EMPTY_GROUP_MEMBER_LOOKUP = Object.freeze({});

export default {
	name: "chatBox",
	components: {
		ChatGroupSide,
		ChatPrivateSide,
		ChatHeader,
		MessageViewport,
		ChatOverlays,
		SelectionToolbar,
		ChatFooter,
		ChatTopMessage,
		ChatGroupRtcBanner
	},
	props: {
		chat: {
			type: Object
		}
	},
	data() {
		return {
			disposeDismiss: null,
			userInfo: {},
			groupId: null,
			groupInfoLoading: false,
			groupInfoError: false,
			groupInfoErrorMessage: '',
			groupInfoRequestToken: 0,
			headerResizeObserver: null,
			headerLayoutSnapshot: null,
			headerLayoutSnapshotFrame: null,
			sendImageUrl: "",
			sendImageFile: "",
			placeholder: "",
			isReceipt: true, // 是否回执消息
			showRecord: false, // 是否显示语音录制弹窗
			showSide: false, // 是否显示群聊信息栏
			showTopMessageDialog: false, // 是否显示置顶消息弹窗
			topMessageList: [], // 置顶消息列表
			topMessageLoading: false, // 置顶消息加载状态
			removingTopMessageId: null, // 当前置顶弹窗中正在取消置顶的消息
			showMinIdx: 0, // 下标低于showMinIdx的消息不显示，否则页面会很卡
			showMaxIdx: -1, // 下标大于showMaxIdx的消息不显示，否则可能很卡,-1表示不限制
			activeMessageIdx: -1, //选择消息idx（单条选中，用于定位）
			selectedMessages: [], // 多选消息数组（使用数组以支持Vue响应式）
			isMultiSelectMode: false, // 是否处于多选模式
			quoteMessage: null, // 被引用的消息
			reqQueue: [], // 等待发送的请求队列
			isSending: false, // 是否正在发消息
			isInBottom: true, // 滚动条是否在底部
			newMessageSize: 0, // 滚动条不在底部时新的消息数量
			maxTmpId: 0, // 最后生成的临时id
			topMessageSyncing: false, // 置顶消息同步中的过渡态
			lastSendAtMap: {}, // 群消息发送频率限制缓存
			attachmentRetryMap: new Map(), // 仅前端运行期的附件重试缓存
			historyLoading: false,
			historyLoadingVisible: false,
			historyLoadingToken: 0,
			historyLoadingTimer: null,
			historyLoadingVisibleAt: 0,
			historyFinished: false,
			historyCursor: '',
			historyChatKey: '',
			historyError: false,
			bottomWindowLoadingVisible: false,
			bottomWindowLoadingToken: 0,
			bottomWindowExpanding: false,
			isNearHistoryTop: false,
			suppressMessageGrowthScroll: false,
			readRequest: null
		}
	},
	methods: {
		checkSendRateLimit() {
			if (!this.isGroup) {
				return true;
			}
			if (this.isOwner || this.isManager) {
				return true;
			}
			const limitSeconds = this.configStore.globalConfig?.group?.sendRateLimit ?? 0;
			if (limitSeconds <= 0) {
				return true;
			}
			const groupKey = String(this.activeGroupId || '');
			if (!groupKey) {
				return true;
			}
			const now = Date.now();
			const lastSendAt = this.lastSendAtMap[groupKey] || 0;
			const diff = now - lastSendAt;
			if (diff < limitSeconds * 1000) {
				const remain = Math.ceil((limitSeconds * 1000 - diff) / 1000);
				this.$message.warning(`发送过于频繁，请 ${remain} 秒后再试`);
				return false;
			}
			this.lastSendAtMap[groupKey] = now;
			return true;
		},
		resolveUploadSize(limitMb, fallbackMb) {
			const parsedLimit = Number(limitMb);
			if (parsedLimit === 0) return 0;  // 0 = 禁用
			const safeLimit = Number.isInteger(parsedLimit) && parsedLimit > 0 ? parsedLimit : fallbackMb;
			return safeLimit * 1024 * 1024;
		},
		isAttachmentMessageType(type) {
			return type == this.$enums.MESSAGE_TYPE.IMAGE
				|| type == this.$enums.MESSAGE_TYPE.VIDEO
				|| type == this.$enums.MESSAGE_TYPE.FILE;
		},
		buildMessageSendPayload(msgInfo, chatInfo = this.chat) {
			const chatType = chatInfo && chatInfo.type
				? String(chatInfo.type).toUpperCase()
				: (msgInfo && msgInfo.groupId !== undefined ? 'GROUP' : 'PRIVATE');
			const localId = ensureStableMessageLocalId(msgInfo, chatInfo);
			const payload = {
				content: msgInfo && msgInfo.content,
				type: msgInfo && msgInfo.type,
				localId
			};
			if (msgInfo && msgInfo.quoteMessageId !== undefined && msgInfo.quoteMessageId !== null) {
				payload.quoteMessageId = msgInfo.quoteMessageId;
			}
			if (chatType === 'GROUP') {
				payload.groupId = msgInfo && msgInfo.groupId !== undefined ? msgInfo.groupId : chatInfo && chatInfo.targetId;
				payload.receipt = Boolean(msgInfo && msgInfo.receipt);
				if (msgInfo && Array.isArray(msgInfo.atUserIds)) {
					payload.atUserIds = msgInfo.atUserIds;
				}
			} else {
				payload.recvId = msgInfo && msgInfo.recvId !== undefined ? msgInfo.recvId : chatInfo && chatInfo.targetId;
			}
			return payload;
		},
		sendMessagePayload(chatInfo, msgInfo) {
			const chatType = chatInfo && chatInfo.type
				? String(chatInfo.type).toLowerCase()
				: (msgInfo && msgInfo.groupId !== undefined ? 'group' : 'private');
			return this.$http({
				url: `/message/${chatType}/send`,
				method: 'post',
				data: this.buildMessageSendPayload(msgInfo, chatInfo)
			});
		},
		normalizeForwardMessages(messages = []) {
			return normalizeForwardMessages(messages);
		},
		extractForwardErrorMessage(error) {
			return extractForwardErrorMessage(error);
		},
		async sendForwardMessages(chats, messages, options = {}) {
			const forwardTarget = prepareForwardTargets(chats);
			const targets = forwardTarget.targets;
			const forwardMessages = this.normalizeForwardMessages(messages);
			if (targets.length === 0) {
				this.$message.warning('请选择转发目标');
				return null;
			}
			if (forwardMessages.length === 0) {
				this.$message.warning('没有可转发的消息');
				return null;
			}
			if (forwardTarget.truncated) {
				this.$message.warning(`已选择 ${forwardTarget.originalCount} 个会话，本次仅转发前 ${forwardTarget.limit} 个会话`);
			}
			try {
				return await this.$http({
					url: '/message/forward',
					method: 'post',
					data: {
						targets,
						messages: forwardMessages
					},
					_skipErrorMessage: true
				});
			} catch (error) {
				this.$message.error(this.extractForwardErrorMessage(error) || options.failText || '转发失败');
				return null;
			}
		},
		getAttachmentStateText(type, state) {
			const labelMap = {
				[this.$enums.MESSAGE_TYPE.IMAGE]: '图片',
				[this.$enums.MESSAGE_TYPE.VIDEO]: '视频',
				[this.$enums.MESSAGE_TYPE.FILE]: '文件'
			};
			const label = labelMap[type] || '附件';
			if (state === 'failed') {
				return `${label}上传失败`;
			}
			return `${label}上传中`;
		},
		shouldKeepLocalAttachmentPreview() {
			return typeof window !== 'undefined' && Boolean(window.electronAPI);
		},
		attachLocalPreview(msgInfo, previewContent) {
			if (!msgInfo || !this.shouldKeepLocalAttachmentPreview()) {
				return;
			}
			msgInfo.localPreviewContent = previewContent || msgInfo.content || '';
		},
		copyLocalPreview(fromMessage, toMessage) {
			if (!fromMessage || !toMessage || !this.shouldKeepLocalAttachmentPreview()) {
				return;
			}
			if (fromMessage.localPreviewContent) {
				toMessage.localPreviewContent = fromMessage.localPreviewContent;
			}
		},
		rememberAttachmentRetry(msgInfo, file) {
			if (!msgInfo || !msgInfo.tmpId || !file) {
				return;
			}
			this.attachmentRetryMap.set(String(msgInfo.tmpId), {
				file,
				type: msgInfo.type
			});
		},
		resolveAttachmentRetry(msgInfo) {
			if (!msgInfo || !msgInfo.tmpId) {
				return null;
			}
			return this.attachmentRetryMap.get(String(msgInfo.tmpId)) || null;
		},
		forgetAttachmentRetry(msgInfo) {
			if (!msgInfo || !msgInfo.tmpId) {
				return;
			}
			this.attachmentRetryMap.delete(String(msgInfo.tmpId));
		},
		resolveUploadContext(filePayload) {
			const source = filePayload && filePayload.file ? filePayload.file : filePayload;
			return {
				file: source || null,
				msgInfo: source && source.msgInfo ? source.msgInfo : (filePayload && filePayload.msgInfo ? filePayload.msgInfo : null),
				chat: source && source.chat ? source.chat : (filePayload && filePayload.chat ? filePayload.chat : null)
			};
		},
		requireUploadContext(filePayload, label) {
			const context = this.resolveUploadContext(filePayload);
			if (!context.msgInfo || !context.chat) {
				this.$message.error(`${label}发送失败，请重试`);
				return null;
			}
			return context;
		},
		retryAttachmentUpload(msgInfo) {
			const retryTask = this.resolveAttachmentRetry(msgInfo);
			if (!retryTask || !retryTask.file) {
				this.$message.error('附件缓存已失效，请重新选择后发送');
				return;
			}
			if (!this.checkSendRateLimit()) {
				return;
			}
			const sourceFile = retryTask.file;
			const sourceType = retryTask.type;
			this.forgetAttachmentRetry(msgInfo);
			this.chatStore.deleteMessage(msgInfo, this.chat);
			switch (sourceType) {
				case this.$enums.MESSAGE_TYPE.IMAGE:
					this.sendImageMessage(sourceFile).catch(() => {});
					return;
				case this.$enums.MESSAGE_TYPE.VIDEO:
					this.sendVideoMessage(sourceFile).catch(() => {});
					return;
				case this.$enums.MESSAGE_TYPE.FILE:
					this.sendFileMessage(sourceFile).catch(() => {});
					return;
				default:
					this.$message.error('该附件暂不支持自动重试');
			}
		},
		resendExistingMessage(msgInfo) {
			if (!msgInfo) {
				return;
			}
			if (!this.checkSendRateLimit()) {
				return;
			}
			const chat = this.chat;
			this.forgetAttachmentRetry(msgInfo);
			this.chatStore.deleteMessage(msgInfo, chat);
			const resendPayload = JSON.parse(JSON.stringify(msgInfo));
			resendPayload.id = null;
			resendPayload.tmpId = this.generateId();
			resendPayload.localAssetState = '';
			resendPayload.localAssetText = '';
			resendPayload.localAssetRetryable = false;
			const tmpMessage = this.buildTmpMessage(resendPayload);
			this.chatStore.insertMessage(tmpMessage, chat);
			this.sendMessageRequest(resendPayload, chat).then((m) => {
				tmpMessage.id = m.id;
				tmpMessage.status = m.status;
				tmpMessage.content = m.content || tmpMessage.content;
				tmpMessage.quoteMessage = m.quoteMessage || tmpMessage.quoteMessage;
				this.chatStore.updateMessage(tmpMessage, chat);
			}).catch((error) => {
				this.handleSendError(error, tmpMessage, chat);
			}).finally(() => {
				this.scrollToBottom();
			});
		},
		getChatOverlaysRef() {
			return this.$refs.chatOverlays || null;
		},
		getChatFooterRef() {
			return this.$refs.chatFooter || null;
		},
		normalizeChatMessages(chat = this.chat) {
			if (!chat || !Array.isArray(chat.messages) || chat.type != 'GROUP') {
				return;
			}
			const currentGroupId = chat.targetId;
			if (!currentGroupId) {
				return;
			}
			const normalizedMessages = [];
			chat.messages.forEach((message) => {
				if (!message) {
					return;
				}
				const isNormalMessage = this.$msgType.isNormal(message.type) || this.$msgType.isAction(message.type);
				if (!isNormalMessage) {
					normalizedMessages.push(message);
					return;
				}
				// 群聊中的普通消息必须绑定当前群，避免本地异常数据串入当前会话。
				if (!!message.groupId && String(message.groupId) == String(currentGroupId)) {
					normalizedMessages.push(message);
				}
			});
			if (normalizedMessages.length === chat.messages.length) {
				return;
			}
			chat.messages.splice(0, chat.messages.length, ...normalizedMessages);
		},
		resolveHistoryMessageSeqNo(message) {
			const seqNo = Number(message && (message.seqNo || message.id || message.sendTime || 0));
			return Number.isFinite(seqNo) ? seqNo : 0;
		},
		resolveLoadedHistoryState(chat) {
			const state = {
				realMessageCount: 0,
				minId: 0,
				minSeqNo: 0
			};
			if (!chat || !Array.isArray(chat.messages)) {
				return state;
			}
			chat.messages.forEach((message) => {
				if (!message || message.type == this.$enums.MESSAGE_TYPE.TIP_TIME || !message.id) {
					return;
				}
				state.realMessageCount += 1;
				const messageId = Number(message.id);
				if (messageId > 0 && (!state.minId || messageId < state.minId)) {
					state.minId = messageId;
				}
				const seqNo = this.resolveHistoryMessageSeqNo(message);
				if (seqNo > 0 && (!state.minSeqNo || seqNo < state.minSeqNo)) {
					state.minSeqNo = seqNo;
				}
			});
			return state;
		},
		filterVisibleHistoryMessages(messages, historyCutoffSeqNo = 0) {
			const list = Array.isArray(messages) ? messages : [];
			if (historyCutoffSeqNo <= 0) {
				return list;
			}
			return list.filter((message) => this.resolveHistoryMessageSeqNo(message) > historyCutoffSeqNo);
		},
		isCardMessageType(messageType) {
			return messageType == this.$enums.MESSAGE_TYPE.USER_CARD ||
				messageType == this.$enums.MESSAGE_TYPE.GROUP_CARD;
		},
		canForwardCardMessages() {
			return this.$permission.canForwardCardMessage
				? this.$permission.canForwardCardMessage()
				: this.$permission.hasRolePermission('message.cardForward');
		},
		resolveSelectedMessageState() {
			const state = {
				count: this.selectedMessages.length,
				deleteIds: [],
				batchForwardableMessages: [],
				sentMessages: [],
				unsentMessages: []
			};
			const unsupportedForwardTypes = new Set([
				this.$enums.MESSAGE_TYPE.ACT_RT_VOICE,
				this.$enums.MESSAGE_TYPE.ACT_RT_VIDEO
			]);
			const canForwardCard = this.canForwardCardMessages();
			this.selectedMessages.forEach((message) => {
				if (!message) {
					return;
				}
				if (message.id) {
					state.deleteIds.push(message.id);
				}
				if (!message.id) {
					state.unsentMessages.push(message);
					return;
				}
				state.sentMessages.push(message);
				if (!unsupportedForwardTypes.has(message.type) && (!this.isCardMessageType(message.type) || canForwardCard)) {
					state.batchForwardableMessages.push(message);
				}
			});
			return state;
		},
		consumeDeferredInitialGroupSync(chat) {
			const shouldDefer = Boolean(chat && chat._deferInitialGroupSync);
			if (shouldDefer) {
				delete chat._deferInitialGroupSync;
			}
			return shouldDefer;
		},
		isActiveGroupChat(groupId) {
			return this.chat && this.chat.type == 'GROUP' && String(this.chat.targetId) == String(groupId);
		},
		resolveCurrentGroupId() {
			return this.isGroup && this.chat && this.chat.targetId ? this.chat.targetId : null;
		},
		runAfterPaint(task, frames = 2) {
			const safeFrames = Number.isFinite(Number(frames)) ? Math.max(1, Math.floor(frames)) : 1;
			if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
				setTimeout(task, 16 * safeFrames);
				return;
			}
			let remain = safeFrames;
			const step = () => {
				remain -= 1;
				if (remain <= 0) {
					task();
					return;
				}
				window.requestAnimationFrame(step);
			};
			window.requestAnimationFrame(step);
		},
		runWhenBrowserIdle(task, timeout = 1200) {
			if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
				window.requestIdleCallback(task, { timeout });
				return;
			}
			setTimeout(task, Math.min(timeout, 300));
		},
		getMessageScrollElement() {
			const viewport = this.$refs.messageViewport;
			if (viewport && typeof viewport.getScrollElement === 'function') {
				return viewport.getScrollElement();
			}
			return this.$el ? this.$el.querySelector('#chatScrollBox') : null;
		},
		captureHeaderLayoutSnapshot() {
			const header = this.$el ? this.$el.querySelector('.chat-header-stack') : null;
			const scrollElement = this.getMessageScrollElement();
			if (!header || !scrollElement) {
				return null;
			}
			const distanceFromBottom = Math.max(
				scrollElement.scrollHeight - scrollElement.scrollTop - scrollElement.clientHeight,
				0
			);
			return {
				headerHeight: header.getBoundingClientRect().height,
				wasAtBottom: this.isInBottom || distanceFromBottom <= 30,
				distanceFromBottom
			};
		},
		queueHeaderLayoutSnapshot() {
			if (this.headerLayoutSnapshotFrame !== null) {
				return;
			}
			const update = () => {
				this.headerLayoutSnapshotFrame = null;
				this.headerLayoutSnapshot = this.captureHeaderLayoutSnapshot();
			};
			if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
				this.headerLayoutSnapshotFrame = window.requestAnimationFrame(update);
				return;
			}
			this.headerLayoutSnapshotFrame = setTimeout(update, 16);
		},
		compensateHeaderLayoutChange() {
			const previous = this.headerLayoutSnapshot;
			const current = this.captureHeaderLayoutSnapshot();
			if (!current) {
				return;
			}
			this.headerLayoutSnapshot = current;
			if (!previous || Math.abs(current.headerHeight - previous.headerHeight) < 0.5) {
				return;
			}
			const scrollElement = this.getMessageScrollElement();
			if (!scrollElement) {
				return;
			}
			if (previous.wasAtBottom) {
				this.restoreMessageBottomDistance(scrollElement, previous.distanceFromBottom);
			} else {
				scrollElement.scrollTop += current.headerHeight - previous.headerHeight;
			}
			this.headerLayoutSnapshot = this.captureHeaderLayoutSnapshot();
		},
		queueHeaderLayoutCompensation() {
			this.$nextTick(() => this.compensateHeaderLayoutChange());
		},
		observeHeaderLayout() {
			const header = this.$el ? this.$el.querySelector('.chat-header-stack') : null;
			if (!header) {
				return;
			}
			this.headerLayoutSnapshot = this.captureHeaderLayoutSnapshot();
			if (typeof window !== 'undefined' && typeof window.ResizeObserver === 'function') {
				this.headerResizeObserver = new window.ResizeObserver(() => {
					this.compensateHeaderLayoutChange();
				});
				this.headerResizeObserver.observe(header);
			}
		},
		warmupGroupMembersAfterPaint(groupId, options = {}) {
			if (!groupId) {
				return;
			}
			const {
				refreshOnline = false
			} = options;
			this.runAfterPaint(() => {
				if (!this.isActiveGroupChat(groupId)) {
					return;
				}
				this.runWhenBrowserIdle(() => {
					if (!this.isActiveGroupChat(groupId)) {
						return;
					}
					const group = this.groupStore.findGroup(groupId);
					if (!group || !group.id) {
						return;
					}
					this.groupStore.refreshMember(groupId, {
						refreshOnline,
						pageLimit: 30
					}).catch(() => []);
				});
			});
		},
		canRefreshGroupOnlineCount(groupId) {
			if (!groupId || !this.isGroup) {
				return false;
			}
			return this.$permission.hasGroupPermission(groupId, 'isAllowViewOnlineCount');
		},
		refreshGroupOnlineCount(groupId, options = {}) {
			if (!this.canRefreshGroupOnlineCount(groupId)) {
				return Promise.resolve([]);
			}
			return this.groupStore.refreshMemberOnline(groupId, {
				deferApply: options.deferApply !== false,
				chunkSize: options.chunkSize
			}).catch(() => []);
		},
		resolveDismissZIndex() {
			if (!this.$el) {
				return this.configStore.isMobile ? 260 : 240;
			}
			return resolveZIndex([
				this.$el.querySelector('.chat-mobile-side-overlay'),
				this.$el.querySelector('.chat-mobile-side-drawer'),
				this.$el.querySelector('.chat-side-box')
			], this.configStore.isMobile ? 260 : 240);
		},
		onClickChatBox(event) {
			this.closeSideOnOutsideClick(event);
			// 关闭表情窗口
			const chatOverlays = this.getChatOverlaysRef();
			if (chatOverlays && chatOverlays.closeEmotion) {
				chatOverlays.closeEmotion();
			}
			// 取消消息选中
			this.activeMessageIdx = -1;
			// 如果不在多选模式，点击空白区域退出多选模式
			if (this.isMultiSelectMode) {
				this.exitMultiSelectMode();
			}
		},
		closeSideOnOutsideClick(event) {
			if (!this.showSide || this.configStore.isMobile || !event || !this.$el) {
				return;
			}
			const target = event.target;
			const sideBox = this.$el.querySelector('.chat-side-box');
			if (sideBox && sideBox.contains(target)) {
				return;
			}
			if (target && target.closest && target.closest('.btn-side')) {
				return;
			}
			this.showSide = false;
		},
		enterMultiSelectMode(msgInfo) {
			this.isMultiSelectMode = true;
			this.selectedMessages = [];
			this.toggleMessageSelect(msgInfo);
		},
		exitMultiSelectMode() {
			this.isMultiSelectMode = false;
			this.selectedMessages = [];
		},
		isAllowSelectMessage(msgInfo) {
			return this.$msgType.isNormal(msgInfo.type) || this.$msgType.isAction(msgInfo.type);
		},
		toggleMessageSelect(msgInfo) {
			if (!this.isMultiSelectMode) {
				this.enterMultiSelectMode(msgInfo);
				return;
			}
			const index = this.selectedMessages.indexOf(msgInfo);
			if (index > -1) {
				// 取消选中
				this.selectedMessages.splice(index, 1);
			} else {
				// 检查是否已达到最大选择数量
				if (this.selectedMessages.length >= 20) {
					this.$message.warning('最多只能选择20条消息');
					return;
				}
				// 选中
				this.selectedMessages.push(msgInfo);
			}
			// 如果没有选中任何消息，退出多选模式
			if (this.selectedMessages.length === 0) {
				this.exitMultiSelectMode();
			}
		},
		isMessageSelected(msgInfo) {
			return this.selectedMessages.indexOf(msgInfo) > -1;
		},
		onCall(type) {
			if (type == this.$enums.MESSAGE_TYPE.ACT_RT_VOICE) {
				this.showPrivateVideo('voice');
			} else if (type == this.$enums.MESSAGE_TYPE.ACT_RT_VIDEO) {
				this.showPrivateVideo('video');
			}
		},
		onSwitchReceipt() {
			this.isReceipt = !this.isReceipt;
			this.refreshPlaceHolder();
		},
		onImageSuccess(data, filePayload) {
			const context = this.requireUploadContext(filePayload, '图片');
			if (!context) {
				return;
			}
			let msgInfo = JSON.parse(JSON.stringify(context.msgInfo));
			this.copyLocalPreview(context.msgInfo, msgInfo);
			msgInfo.content = JSON.stringify(data);
			msgInfo.receipt = this.isReceipt;
			msgInfo.localAssetState = '';
			msgInfo.localAssetText = '';
			msgInfo.localAssetRetryable = false;
			this.chatStore.updateMessage(msgInfo, context.chat);
			this.forgetAttachmentRetry(context.msgInfo);
			this.sendMessageRequest(msgInfo, context.chat).then(m => {
				msgInfo.id = m.id;
				msgInfo.status = m.status;
				msgInfo.content = m.content || msgInfo.content;
				msgInfo.quoteMessage = m.quoteMessage || msgInfo.quoteMessage;
				this.isReceipt = false;
				this.chatStore.updateMessage(msgInfo, context.chat);
			}).catch((error) => {
				this.handleSendError(error, msgInfo, context.chat);
			})
		},
		onImageFail(e, filePayload) {
			const context = this.requireUploadContext(filePayload, '图片');
			if (!context) {
				return;
			}
			let msgInfo = JSON.parse(JSON.stringify(context.msgInfo));
			this.copyLocalPreview(context.msgInfo, msgInfo);
			msgInfo.status = this.$enums.MESSAGE_STATUS.FAILED;
			msgInfo.localAssetState = 'failed';
			msgInfo.localAssetText = this.getAttachmentStateText(msgInfo.type, 'failed');
			msgInfo.localAssetRetryable = true;
			this.chatStore.updateMessage(msgInfo, context.chat);
		},
		onImageBefore(file) {
			let url = URL.createObjectURL(file);
			let data = {
				originUrl: url,
				thumbUrl: url
			}
			let msgInfo = {
				tmpId: this.generateId(),
				sendId: this.mine.id,
				content: JSON.stringify(data),
				sendTime: new Date().getTime(),
				selfSend: true,
				type: this.$enums.MESSAGE_TYPE.IMAGE,
				readedCount: 0,
				status: this.$enums.MESSAGE_STATUS.SENDING,
				localAssetState: 'uploading',
				localAssetText: this.getAttachmentStateText(this.$enums.MESSAGE_TYPE.IMAGE, 'uploading'),
				localAssetRetryable: false
			}
			this.attachLocalPreview(msgInfo, msgInfo.content);
			// 填充对方id
			this.fillTargetId(msgInfo, this.chat.targetId);
			// 插入消息
			this.chatStore.insertMessage(msgInfo, this.chat);
			// 借助file对象保存
			file.msgInfo = msgInfo;
			file.chat = this.chat;
			this.rememberAttachmentRetry(msgInfo, file);
			// 更新图片尺寸
			let chat = this.chat;
			this.getImageSize(file).then(size => {
				data.width = size.width;
				data.height = size.height;
				msgInfo.content = JSON.stringify(data)
				this.chatStore.updateMessage(msgInfo, chat);
				this.scrollToBottom();
			})
		},
		onFileSuccess(url, filePayload) {
			const context = this.requireUploadContext(filePayload, '文件');
			if (!context) {
				return;
			}
			const sourceFile = context.file || {};
			let data = {
				name: sourceFile.name,
				size: sourceFile.size,
				url: url
			}
			let msgInfo = JSON.parse(JSON.stringify(context.msgInfo));
			this.copyLocalPreview(context.msgInfo, msgInfo);
			msgInfo.content = JSON.stringify(data);
			msgInfo.receipt = this.isReceipt
			msgInfo.localAssetState = '';
			msgInfo.localAssetText = '';
			msgInfo.localAssetRetryable = false;
			this.chatStore.updateMessage(msgInfo, context.chat);
			this.forgetAttachmentRetry(context.msgInfo);
			this.sendMessageRequest(msgInfo, context.chat).then((m) => {
				msgInfo.id = m.id;
				msgInfo.status = m.status;
				msgInfo.content = m.content || msgInfo.content;
				msgInfo.quoteMessage = m.quoteMessage || msgInfo.quoteMessage;
				this.isReceipt = false;
				this.refreshPlaceHolder();
				this.chatStore.updateMessage(msgInfo, context.chat);
			}).catch((error) => {
				this.handleSendError(error, msgInfo, context.chat);
			})
		},
		onFileFail(e, filePayload) {
			const context = this.requireUploadContext(filePayload, '文件');
			if (!context) {
				return;
			}
			let msgInfo = JSON.parse(JSON.stringify(context.msgInfo));
			this.copyLocalPreview(context.msgInfo, msgInfo);
			msgInfo.status = this.$enums.MESSAGE_STATUS.FAILED;
			msgInfo.localAssetState = 'failed';
			msgInfo.localAssetText = this.getAttachmentStateText(msgInfo.type, 'failed');
			msgInfo.localAssetRetryable = true;
			this.chatStore.updateMessage(msgInfo, context.chat);
		},
		onFileBefore(file) {
			let url = URL.createObjectURL(file);
			let data = {
				name: file.name,
				size: file.size,
				url: url
			}
			let msgInfo = {
				tmpId: this.generateId(),
				sendId: this.mine.id,
				content: JSON.stringify(data),
				sendTime: new Date().getTime(),
				selfSend: true,
				type: this.$enums.MESSAGE_TYPE.FILE,
				readedCount: 0,
				status: this.$enums.MESSAGE_STATUS.SENDING,
				localAssetState: 'uploading',
				localAssetText: this.getAttachmentStateText(this.$enums.MESSAGE_TYPE.FILE, 'uploading'),
				localAssetRetryable: false
			}
			// 填充对方id
			this.fillTargetId(msgInfo, this.chat.targetId);
			// 插入消息
			this.chatStore.insertMessage(msgInfo, this.chat);
			// 借助file对象透传
			file.msgInfo = msgInfo;
			file.chat = this.chat;
			this.rememberAttachmentRetry(msgInfo, file);
		},
		onVideoSuccess(data, filePayload) {
			const context = this.requireUploadContext(filePayload, '视频');
			if (!context) {
				return;
			}
			let msgInfo = JSON.parse(JSON.stringify(context.msgInfo));
			this.copyLocalPreview(context.msgInfo, msgInfo);
			msgInfo.content = JSON.stringify(data);
			msgInfo.receipt = this.isReceipt;
			msgInfo.localAssetState = '';
			msgInfo.localAssetText = '';
			msgInfo.localAssetRetryable = false;
			this.chatStore.updateMessage(msgInfo, context.chat);
			this.forgetAttachmentRetry(context.msgInfo);
			this.sendMessageRequest(msgInfo, context.chat).then(m => {
				msgInfo.id = m.id;
				msgInfo.status = m.status;
				msgInfo.content = m.content || msgInfo.content;
				msgInfo.quoteMessage = m.quoteMessage || msgInfo.quoteMessage;
				this.isReceipt = false;
				this.chatStore.updateMessage(msgInfo, context.chat);
			}).catch((error) => {
				this.handleSendError(error, msgInfo, context.chat);
			})
		},
		onVideoFail(e, filePayload) {
			const context = this.requireUploadContext(filePayload, '视频');
			if (!context) {
				return;
			}
			let msgInfo = JSON.parse(JSON.stringify(context.msgInfo));
			this.copyLocalPreview(context.msgInfo, msgInfo);
			msgInfo.status = this.$enums.MESSAGE_STATUS.FAILED;
			msgInfo.localAssetState = 'failed';
			msgInfo.localAssetText = this.getAttachmentStateText(msgInfo.type, 'failed');
			msgInfo.localAssetRetryable = true;
			this.chatStore.updateMessage(msgInfo, context.chat);
		},
		onVideoBefore(file) {
			let url = URL.createObjectURL(file);
			let data = {
				videoUrl: url
			}
			let msgInfo = {
				tmpId: this.generateId(),
				sendId: this.mine.id,
				content: JSON.stringify(data),
				sendTime: new Date().getTime(),
				selfSend: true,
				type: this.$enums.MESSAGE_TYPE.VIDEO,
				readedCount: 0,
				status: this.$enums.MESSAGE_STATUS.SENDING,
				localAssetState: 'uploading',
				localAssetText: this.getAttachmentStateText(this.$enums.MESSAGE_TYPE.VIDEO, 'uploading'),
				localAssetRetryable: false
			}
			this.attachLocalPreview(msgInfo, msgInfo.content);
			// 填充对方id
			this.fillTargetId(msgInfo, this.chat.targetId);
			// 插入消息
			this.chatStore.insertMessage(msgInfo, this.chat);
			// 借助file对象透传
			file.msgInfo = msgInfo;
			file.chat = this.chat;
			this.rememberAttachmentRetry(msgInfo, file);
			// 更新视频本地尺寸和封面
			let chat = this.chat;
			this.getVideoPreviewInfo(file).then(preview => {
				if (!preview) {
					return;
				}
				if (preview.width && preview.height) {
					data.width = preview.width;
					data.height = preview.height;
				}
				if (preview.coverUrl) {
					data.coverUrl = preview.coverUrl;
				}
				msgInfo.content = JSON.stringify(data);
				msgInfo.localPreviewContent = msgInfo.content;
				this.chatStore.updateMessage(msgInfo, chat);
				this.scrollToBottom();
			}).catch(() => {});
		},
		onClickMore() {
			this.showSide = !this.showSide;
			if (this.showSide && this.isGroup) {
				const groupId = this.activeGroupId;
				if (!groupId) {
					return;
				}
				// 刷新一下群和成员信息
				this.loadGroup(groupId, {
					refreshOnline: true
				});
			} else if (this.showSide && this.isPrivate) {
				this.loadFriend(this.chat.targetId, {
					forceRemote: true
				});
			}
		},
		onScroll(e) {
			let scrollElement = e.target
			let scrollTop = scrollElement.scrollTop
			const isNearBottom = scrollTop + scrollElement.clientHeight >= scrollElement.scrollHeight - 30;
			this.isNearHistoryTop = scrollTop < TOP_HISTORY_LOAD_THRESHOLD;
			if (!isNearBottom) {
				this.isInBottom = false;
			}
			// 滚到顶部
			if (scrollTop < TOP_HISTORY_LOAD_THRESHOLD) {
				const scrollAnchor = this.captureMessageScrollAnchor(scrollElement);
				const previousMinIdx = this.showMinIdx;
				// 每次按一个历史分页单位扩展可见消息
				this.expandMessageWindowAtTop();
				if (this.showMinIdx < previousMinIdx) {
					this.$nextTick(() => {
						this.restoreMessageScrollAnchor(scrollElement, scrollAnchor);
					});
				}
				if (this.canAutoLoadOlderHistory) {
					this.loadOlderHistoryFromScroll(scrollElement);
				}
				// 清除底部标志
				this.isInBottom = false;
				this.refreshAtMessage();
			}
			// 滚到底部
			if (isNearBottom) {
				if (!this.bottomWindowExpanding) {
					const distanceFromBottom = Math.max(scrollElement.scrollHeight - scrollTop - scrollElement.clientHeight, 0);
					const expanded = this.expandMessageWindowAtBottom();
					const reachedConversationBottom = this.showMaxIdx < 0;
					if (expanded) {
						const loadingToken = this.beginBottomWindowLoading();
						const loadingStartedAt = Date.now();
						this.bottomWindowExpanding = true;
						this.$nextTick(() => {
							if (reachedConversationBottom) {
								scrollElement.scrollTop = scrollElement.scrollHeight;
							} else {
								this.restoreMessageBottomDistance(scrollElement, distanceFromBottom);
							}
							this.bottomWindowExpanding = false;
							this.finishBottomWindowLoading(loadingToken, loadingStartedAt);
						});
					}
				}
				// 设置底部标志
				if (this.showMaxIdx < 0) {
					this.isInBottom = true;
					this.newMessageSize = 0;
				}
			}
			this.queueHeaderLayoutSnapshot();
		},
		showEmotionBox() {
			const chatFooter = this.getChatFooterRef();
			const chatOverlays = this.getChatOverlaysRef();
			const emotionTrigger = chatFooter && chatFooter.getEmotionTrigger ? chatFooter.getEmotionTrigger() : null;
			if (!emotionTrigger || !chatOverlays || !chatOverlays.openEmotion) {
				return;
			}
			let width = emotionTrigger.offsetWidth;
			let left = this.$elm.fixLeft(emotionTrigger);
			let top = this.$elm.fixTop(emotionTrigger);
			chatOverlays.openEmotion({
				x: left + width / 2,
				y: top
			})
		},
		onScreenshot() {
			const chatFooter = this.getChatFooterRef();
			if (!chatFooter || !chatFooter.pasteScreenShot) {
				return;
			}
			window.electronAPI.invoke('screenshot').then((buffer) => {
				if (buffer) {
					// 粘贴截图到聊天编辑框
					chatFooter.pasteScreenShot(buffer);
				}
			})
		},
		onShowScreenshotMenu(e) {
			let menuItems = [{
				key: 'SCREEN_SHOT',
				name: '截图时隐藏当前窗口',
				icon: 'el-icon-view'
			}];
			const chatOverlays = this.getChatOverlaysRef();
			if (!chatOverlays || !chatOverlays.openRightMenu) {
				return;
			}
			chatOverlays.openRightMenu(e, menuItems)
		},
		screenshotAndHideWin() {
			const chatFooter = this.getChatFooterRef();
			if (!chatFooter || !chatFooter.pasteScreenShot) {
				return;
			}
			// 隐藏当前窗口
			window.electronAPI.sendEvent("close");
			setTimeout(() => {
				window.electronAPI.invoke('screenshot').then((buffer) => {
					if (buffer) {
						// 粘贴截图到聊天编辑框
						chatFooter.pasteScreenShot(buffer);
					}
					// 截图完成后还原窗口
					window.electronAPI.sendEvent("show");
				})
			}, 100)
		},
		onSelectMenu(item) {
			switch (item.key) {
				case "SCREEN_SHOT":
					this.screenshotAndHideWin();
					break;
			}
		},
		onEmotion(payload) {
			if (typeof payload === 'string') {
				const chatFooter = this.getChatFooterRef();
				if (!chatFooter || !chatFooter.insertEmoji) {
					return;
				}
				chatFooter.insertEmoji(payload);
				return;
			}
			if (payload && payload.type === 'sticker' && payload.data) {
				this.sendStickerMessage(payload.data);
			}
		},
		showRecordBox() {
			this.showRecord = true;
		},
		closeRecordBox() {
			this.showRecord = false;
		},
		showPrivateVideo(mode) {
			if (!this.isFriend) {
				this.$message.error("你已不是对方好友,无法呼叫");
				return
			}
			if (this.userStore.isInRtc) {
				this.$message.error("您已在通话中");
				return;
			}
			let rtcInfo = {
				mode: mode,
				isHost: true,
				friend: this.friend,
			}
			// 通过home.vue打开单人视频窗口
			this.$eventBus.$emit("openPrivateVideo", rtcInfo);
		},
		onGroupVideo() {
			if (this.userStore.isInRtc) {
				this.$message.error("您已在通话中");
				return;
			}
			const groupId = this.activeGroupId;
			if (!groupId) {
				return;
			}
			this.$http({
				url: "/webrtc/group/info?groupId=" + groupId,
				method: 'GET',
				// 群通话入口统一展示业务提示，避免与 HTTP 拦截器的通用网络提示重复。
				_skipErrorMessage: true
			}).then((rtcInfo) => {
				const chatOverlays = this.getChatOverlaysRef();
				if (rtcInfo.isChating) {
					// 已在通话中，可以直接加入通话
					if (chatOverlays && chatOverlays.openRtcJoin) {
						chatOverlays.openRtcJoin(rtcInfo);
					}
				} else {
					// 邀请成员发起通话
					let ids = [this.mine.id];
					let maxChannel = this.configStore.webrtc.maxChannel;
					if (chatOverlays && chatOverlays.openRtcSelector) {
						chatOverlays.openRtcSelector(maxChannel, ids, ids, []);
					}
				}
			}).catch((error) => {
				console.warn('读取群通话状态失败', error);
				this.$message.error('无法发起多人通话，请稍后重试');
			})
		},
		onInviteOk(members) {
			const selectedMembers = Array.isArray(members)
				? members
				: (members && Array.isArray(members.items) ? members.items : []);
			// 选择器会锁定当前用户并只返回实际邀请的成员，当前用户不在回调结果中。
			const userInfos = selectedMembers
				.filter(member => member && member.userId !== undefined && member.userId !== null)
				.map(member => ({
					id: member.userId,
					nickName: member.showNickName,
					headImage: member.headImage,
					isCamera: false,
					isMicroPhone: true,
					isShareScreen: false
				}));
			if (userInfos.length === 0) {
				this.$message.warning('请至少选择一名成员');
				return;
			}
			const groupId = this.activeGroupId;
			if (!groupId) {
				return;
			}
			let rtcInfo = {
				isHost: true,
				groupId,
				inviterId: this.mine.id,
				userInfos: userInfos
			}
			// 通过home.vue打开多人视频窗口
			this.$eventBus.$emit("openGroupVideo", rtcInfo);
		},
		showHistoryBox() {
			const chatOverlays = this.getChatOverlaysRef();
			if (!chatOverlays || !chatOverlays.openHistory) {
				return;
			}
			chatOverlays.openHistory();
		},
		onSendRecord(data) {
			if (!this.checkSendRateLimit()) {
				return;
			}
			if (data && data.file) {
				const uploadPayload = {
					duration: data.duration,
					url: data.localUrl || ''
				};
				const msgInfo = {
					tmpId: this.generateId(),
					content: JSON.stringify(uploadPayload),
					type: this.$enums.MESSAGE_TYPE.AUDIO,
					receipt: this.isReceipt,
					localAssetState: 'uploading'
				}
				this.fillTargetId(msgInfo, this.chat.targetId);
				const chat = this.chat;
				const tmpMessage = this.buildTmpMessage(msgInfo);
				this.chatStore.insertMessage(tmpMessage, chat);
				this.showRecord = false;
				this.refreshPlaceHolder();
				uploadProtectedAsset(data.file, {
					action: '/audio/upload'
				}).then((url) => {
					msgInfo.content = JSON.stringify({
						duration: data.duration,
						url
					});
					msgInfo.localAssetState = '';
					tmpMessage.content = msgInfo.content;
					tmpMessage.localAssetState = '';
					this.chatStore.updateMessage(tmpMessage, chat);
					return this.sendMessageRequest(msgInfo, chat);
				}).then((m) => {
					tmpMessage.id = m.id;
					tmpMessage.status = m.status;
					tmpMessage.content = m.content;
					this.chatStore.updateMessage(tmpMessage, chat);
					const chatFooter = this.getChatFooterRef();
					if (chatFooter && chatFooter.focusInput) {
						chatFooter.focusInput();
					}
					this.scrollToBottom();
				}).catch((error) => {
					if (error && error.code === 40004) {
						this.chatStore.deleteMessage(tmpMessage, chat);
						this.chatStore.refreshLastContent(chat);
					} else {
						tmpMessage.localAssetState = 'failed';
						this.handleSendError(error, tmpMessage, chat);
					}
				}).finally(() => {
					this.isReceipt = false;
				});
				return;
			}
			let msgInfo = {
				tmpId: this.generateId(),
				content: JSON.stringify(data),
				type: this.$enums.MESSAGE_TYPE.AUDIO,
				receipt: this.isReceipt
			}
			// 填充对方id
			this.fillTargetId(msgInfo, this.chat.targetId);
			// 防止发送期间用户切换会话导致串扰
			const chat = this.chat;
			// 临时消息回显	
			let tmpMessage = this.buildTmpMessage(msgInfo);
			this.chatStore.insertMessage(tmpMessage, chat);
			this.sendMessageRequest(msgInfo, chat).then(m => {
				// 更新消息
				tmpMessage.id = m.id;
				tmpMessage.status = m.status;
				this.chatStore.updateMessage(tmpMessage, chat);
				// 保持输入框焦点
				const chatFooter = this.getChatFooterRef();
				if (chatFooter && chatFooter.focusInput) {
					chatFooter.focusInput();
				}
				// 滚动到底部
				this.scrollToBottom();
				// 关闭录音窗口
				this.showRecord = false;
				this.isReceipt = false;
				this.refreshPlaceHolder();
			}).catch((error) => {
				this.handleSendError(error, tmpMessage, chat);
			})
		},
		fillTargetId(msgInfo, targetId) {
			if (this.isGroup) {
				msgInfo.groupId = targetId;
			} else {
				msgInfo.recvId = targetId;
			}
		},
		notifySend() {
			const chatFooter = this.getChatFooterRef();
			if (!chatFooter || !chatFooter.submitInput) {
				return;
			}
			chatFooter.submitInput();
		},
		async sendMessage(fullList) {
			if (!fullList || fullList.length === 0) {
				return;
			}
			if (!this.checkSendRateLimit()) {
				return;
			}
			this.resetEditor();
			this.readedMessage();
			fullList.forEach(async msg => {
				switch (msg.type) {
					case "text":
						await this.sendTextMessage(msg.content, msg.atUserIds);
						break;
					case "image":
						await this.sendImageMessage(msg.content.file);
						break;
					case "file":
						if (isVideoFile(msg.content.file)) {
							await this.sendVideoMessage(msg.content.file);
							break;
						}
						await this.sendFileMessage(msg.content.file);
						break;
				}
			})
		},
		sendImageMessage(file) {
			return new Promise((resolve, reject) => {
				if (this.chatImageMaxSize === 0) {
					this.$message.error('图片发送已被管理员禁用');
					reject();
					return;
				}
				if (file.size > this.chatImageMaxSize) {
					this.$message.error(`图片大小不能超过 ${Math.round(this.chatImageMaxSize / 1024 / 1024)}M!`);
					reject();
					return;
				}
				this.onImageBefore(file);
				uploadProtectedAsset(file, {
					action: '/image/upload',
					isPermanent: false
				}).then((data) => {
					this.onImageSuccess(data, file);
					resolve();
				}).catch((res) => {
					this.onImageFail(res, file);
					reject();
				})
				this.$nextTick(() => {
					const chatFooter = this.getChatFooterRef();
					if (chatFooter && chatFooter.focusInput) {
						chatFooter.focusInput();
					}
				});
				this.scrollToBottom();
			});
		},
		sendTextMessage(sendText, atUserIds) {
			return new Promise((resolve, reject) => {
				if (!sendText.trim()) {
					reject();
				}
				let msgInfo = {
					tmpId: this.generateId(),
					content: sendText,
					type: this.$enums.MESSAGE_TYPE.TEXT
				}
				// 填充对方id
				this.fillTargetId(msgInfo, this.chat.targetId);
				// 被@人员列表
				if (this.isGroup) {
					msgInfo.atUserIds = atUserIds;
					msgInfo.receipt = this.isReceipt;
				}
				// 引用消息
				if (this.quoteMessage) {
					msgInfo.quoteMessageId = this.quoteMessage.id
				}
				// 防止发送期间用户切换会话导致串扰
				const chat = this.chat;
				// 回显消息
				let tmpMessage = this.buildTmpMessage(msgInfo);
				this.chatStore.insertMessage(tmpMessage, chat);
				// 发送
				this.sendMessageRequest(msgInfo, chat).then(m => {
					// 更新消息
					tmpMessage.id = m.id;
					tmpMessage.status = m.status;
					tmpMessage.content = m.content;
					tmpMessage.quoteMessage = m.quoteMessage;
					this.chatStore.updateMessage(tmpMessage, chat);
				}).catch((error) => {
					this.handleSendError(error, tmpMessage, chat);
				}).finally(() => {
					this.isReceipt = false;
					this.quoteMessage = null;
					resolve();
				});
			});
		},
		sendFileMessage(file) {
			return new Promise((resolve, reject) => {
				const chatFooter = this.getChatFooterRef();
				if (!chatFooter || !chatFooter.uploadFile) {
					reject();
					return;
				}
				let check = chatFooter.uploadFile(file);
				if (!check) {
					reject();
				}
			})
		},
		sendVideoMessage(file) {
			return new Promise((resolve, reject) => {
				const chatFooter = this.getChatFooterRef();
				if (!chatFooter || !chatFooter.uploadVideo) {
					reject();
					return;
				}
				let check = chatFooter.uploadVideo(file);
				if (!check) {
					reject();
				}
			})
		},
		parseMessageContent(msgInfo) {
			if (!msgInfo || typeof msgInfo.content !== 'string') {
				return {};
			}
			try {
				return JSON.parse(msgInfo.content);
			} catch (error) {
				return {};
			}
		},
		async readBlobAsDataUrl(blob) {
			return new Promise((resolve, reject) => {
				const reader = new FileReader();
				reader.onload = () => resolve(reader.result || '');
				reader.onerror = () => reject(new Error('资源读取失败'));
				reader.readAsDataURL(blob);
			});
		},
		async fetchResolvedBlob(resolved) {
			if (resolved && resolved.blob instanceof Blob) {
				return resolved.blob;
			}
			const resolvedUrl = resolved && resolved.url ? resolved.url : '';
			if (!resolvedUrl) {
				return null;
			}
			const response = await fetch(resolvedUrl, {
				method: 'GET',
				mode: 'cors',
				cache: 'force-cache',
				credentials: 'omit'
			});
			if (!response.ok) {
				throw new Error(`资源读取失败(${response.status})`);
			}
			return response.blob();
		},
		buildMediaClipboardFileName(kind, msgInfo, contentData, blob) {
			if (contentData && contentData.name) {
				return contentData.name;
			}
			const mimeType = blob && blob.type ? blob.type : (kind === 'video' ? 'video/mp4' : 'image/png');
			const extension = mimeType.includes('/') ? mimeType.split('/').pop() : 'bin';
			const messageId = msgInfo && (msgInfo.id || msgInfo.tmpId) ? (msgInfo.id || msgInfo.tmpId) : Date.now();
			return `${kind}-${messageId}.${extension}`;
		},
		async persistMediaClipboard(kind, msgInfo, contentData, resolved, blob) {
			await saveMediaClipboardEntry({
				kind,
				blob,
				fileName: this.buildMediaClipboardFileName(kind, msgInfo, contentData, blob),
				sourceUrl: resolved && resolved.url ? resolved.url : '',
				textFallback: resolved && resolved.url ? resolved.url : ''
			});
		},
		canWriteClipboardBlob(contentType) {
			const clipboardItemCtor = typeof window !== 'undefined' ? window.ClipboardItem : null;
			if (!(navigator.clipboard && navigator.clipboard.write && window.isSecureContext && clipboardItemCtor)) {
				return false;
			}
			if (!contentType) {
				return true;
			}
			if (typeof clipboardItemCtor.supports === 'function') {
				return clipboardItemCtor.supports(contentType);
			}
			return true;
		},
		async normalizeImageClipboardBlob(resolved, blob) {
			if (!(blob instanceof Blob)) {
				return null;
			}
			if (blob.type === 'image/png') {
				return blob;
			}
			const sourceUrl = resolved && resolved.url ? resolved.url : '';
			let tempUrl = '';
			const imageUrl = sourceUrl || URL.createObjectURL(blob);
			if (!sourceUrl) {
				tempUrl = imageUrl;
			}
			try {
				const image = await new Promise((resolve, reject) => {
					const img = new Image();
					img.onload = () => resolve(img);
					img.onerror = () => reject(new Error('图片加载失败'));
					img.src = imageUrl;
				});
				const canvas = document.createElement('canvas');
				canvas.width = image.naturalWidth || image.width || 1;
				canvas.height = image.naturalHeight || image.height || 1;
				const context = canvas.getContext('2d');
				if (!context) {
					return blob;
				}
				context.drawImage(image, 0, 0, canvas.width, canvas.height);
				const pngBlob = await new Promise((resolve) => {
					canvas.toBlob((nextBlob) => resolve(nextBlob || null), 'image/png');
				});
				return pngBlob || blob;
			} finally {
				if (tempUrl) {
					URL.revokeObjectURL(tempUrl);
				}
			}
		},
		legacyCopyText(text) {
			const normalizedText = String(text || '');
			if (!normalizedText) {
				return false;
			}
			const textarea = document.createElement('textarea');
			textarea.value = normalizedText;
			textarea.setAttribute('readonly', 'readonly');
			textarea.style.position = 'fixed';
			textarea.style.opacity = '0';
			textarea.style.pointerEvents = 'none';
			document.body.appendChild(textarea);
			textarea.focus();
			textarea.select();
			let copied = false;
			try {
				copied = document.execCommand('copy');
			} finally {
				document.body.removeChild(textarea);
			}
			return copied;
		},
		async writeTextToClipboard(text) {
			const normalizedText = String(text || '');
			if (!normalizedText) {
				return false;
			}
			if (window.electronAPI?.clipboard?.writeText) {
				await window.electronAPI.clipboard.writeText({
					text: normalizedText
				});
				return true;
			}
			if (navigator.clipboard && navigator.clipboard.writeText && window.isSecureContext) {
				await navigator.clipboard.writeText(normalizedText);
				return true;
			}
			return this.legacyCopyText(normalizedText);
		},
		async writeImageToClipboard(resolved, blob) {
			const sourceBlob = blob instanceof Blob ? blob : await this.fetchResolvedBlob(resolved);
			if (!(sourceBlob instanceof Blob)) {
				throw new Error('图片暂不可用');
			}
			if (window.electronAPI?.clipboard?.writeImage) {
				const dataUrl = await this.readBlobAsDataUrl(sourceBlob);
				await window.electronAPI.clipboard.writeImage({
					dataUrl: dataUrl,
					text: resolved && resolved.url ? resolved.url : ''
				});
				return 'IMAGE';
			}
			const imageBlob = await this.normalizeImageClipboardBlob(resolved, sourceBlob);
			const clipboardItemCtor = typeof window !== 'undefined' ? window.ClipboardItem : null;
			if (imageBlob && this.canWriteClipboardBlob(imageBlob.type || 'image/png')) {
				await navigator.clipboard.write([
					new clipboardItemCtor({
						[imageBlob.type || 'image/png']: imageBlob
					})
				]);
				return 'IMAGE';
			}
			if (await this.writeTextToClipboard(resolved && resolved.url ? resolved.url : '')) {
				return 'LINK';
			}
			throw new Error('当前环境暂不支持复制图片');
		},
		async writeVideoToClipboard(resolved, blob) {
			const sourceBlob = blob instanceof Blob ? blob : await this.fetchResolvedBlob(resolved);
			const clipboardItemCtor = typeof window !== 'undefined' ? window.ClipboardItem : null;
			// 视频在系统剪贴板上的原生支持并不稳定，先尝试二进制写入，失败后回退到资源链接。
			if (sourceBlob && clipboardItemCtor && this.canWriteClipboardBlob(sourceBlob.type || 'video/mp4')) {
				try {
					await navigator.clipboard.write([
						new clipboardItemCtor({
							[sourceBlob.type || 'video/mp4']: sourceBlob
						})
					]);
					return 'VIDEO';
				} catch (error) {
					// ignore and fallback
				}
			}
			if (await this.writeTextToClipboard(resolved && resolved.url ? resolved.url : '')) {
				return 'LINK';
			}
			throw new Error('当前环境暂不支持复制视频');
		},
		async copyImageMessage(msgInfo) {
			const contentData = this.parseMessageContent(msgInfo);
			const imageSource = contentData.originUrl || contentData.thumbUrl || '';
			if (!imageSource) {
				this.$message.error('图片暂不可复制');
				return;
			}
			try {
				const resolved = await this.$assetResolver.resolve(imageSource, {
					kind: 'image'
				});
				const blob = await this.fetchResolvedBlob(resolved);
				if (!(blob instanceof Blob)) {
					throw new Error('图片暂不可复制');
				}
				await this.persistMediaClipboard('image', msgInfo, contentData, resolved, blob);
				const mode = await this.writeImageToClipboard(resolved, blob);
				if (mode === 'LINK') {
					this.$message.success('图片已复制，可在应用内粘贴');
					return;
				}
				this.$message.success('图片已复制到剪贴板');
			} catch (error) {
				this.$message.error(error && error.message ? error.message : '图片复制失败，请重试');
			}
		},
		async copyVideoMessage(msgInfo) {
			const contentData = this.parseMessageContent(msgInfo);
			const videoSource = contentData.videoUrl || '';
			if (!videoSource) {
				this.$message.error('视频暂不可复制');
				return;
			}
			try {
				const resolved = await this.$assetResolver.resolve(videoSource, {
					kind: 'video'
				});
				const blob = await this.fetchResolvedBlob(resolved);
				if (!(blob instanceof Blob)) {
					throw new Error('视频暂不可复制');
				}
				await this.persistMediaClipboard('video', msgInfo, contentData, resolved, blob);
				const mode = await this.writeVideoToClipboard(resolved, blob);
				if (mode === 'LINK') {
					this.$message.success('视频已复制，可在应用内粘贴');
					return;
				}
				this.$message.success('视频已复制到剪贴板');
			} catch (error) {
				this.$message.error(error && error.message ? error.message : '视频复制失败，请重试');
			}
		},
		async onCopyMessage(msgInfo) {
			if (!msgInfo) {
				return;
			}
			if (msgInfo.type == this.$enums.MESSAGE_TYPE.TEXT) {
				try {
					const copied = await this.writeTextToClipboard(msgInfo.content);
					if (!copied) {
						this.$message.error('复制失败，请手动复制');
						return;
					}
					this.$message.success('内容已复制到剪贴板');
				} catch (error) {
					this.$message.error(error && error.message ? error.message : '复制失败，请手动复制');
				}
				return;
			}
			if (msgInfo.type == this.$enums.MESSAGE_TYPE.IMAGE) {
				await this.copyImageMessage(msgInfo);
				return;
			}
			if (msgInfo.type == this.$enums.MESSAGE_TYPE.VIDEO) {
				await this.copyVideoMessage(msgInfo);
				return;
			}
			this.$message.error('该消息暂不支持复制');
		},
		onResendMessage(msgInfo) {
			if (!msgInfo) {
				return;
			}
			if (this.isAttachmentMessageType(msgInfo.type) && msgInfo.localAssetState === 'failed') {
				this.retryAttachmentUpload(msgInfo);
				return;
			}
			if (
				msgInfo.type != this.$enums.MESSAGE_TYPE.TEXT
				&& msgInfo.type != this.$enums.MESSAGE_TYPE.IMAGE
				&& msgInfo.type != this.$enums.MESSAGE_TYPE.VIDEO
				&& msgInfo.type != this.$enums.MESSAGE_TYPE.FILE
				&& msgInfo.type != this.$enums.MESSAGE_TYPE.STICKER
			) {
				this.$message.error("该消息不支持自动重新发送")
				return;
			}
			this.resendExistingMessage(msgInfo);
		},
		onMessageClick(e, msgInfo) {
			if (this.isMultiSelectMode) {
				// 选中
				if (this.isAllowSelectMessage(msgInfo)) {
					this.toggleMessageSelect(msgInfo);
				}
				e.stopPropagation();
			}
		},
		onMultiSelectMessage(msgInfo) {
			if (!this.$permission.hasRolePermission('message.multiSelect')) {
				this.$message.error('您没有消息多选权限');
				return;
			}
			// 进入多选模式
			this.enterMultiSelectMode(msgInfo);
		},
		onDeleteMessage(msgInfo) {
			this.$confirm('确认删除消息?', '删除消息', {
				confirmButtonText: '确定',
				cancelButtonText: '取消',
				type: 'warning'
			}).then(async () => {
				if (msgInfo.id) {
					const data = {
						chatId: this.chat.targetId,
						messageIds: [msgInfo.id]
					}
					await this.$http({
						url: `/message/${this.chat.type.toLowerCase()}/deleteMessage`,
						method: 'delete',
						data: data
					});
				}
				this.chatStore.deleteMessage(msgInfo, this.chat);
				this.chatStore.refreshLastContent(this.chat);
			});
		},
		onAddToSticker(msgInfo) {
			const sticker = JSON.parse(msgInfo.content);
			this.$http({
				url: '/sticker/custom/add',
				method: 'post',
				data: sticker
			}).then(() => {
				this.$message.success('已添加到自定义表情');
				// 刷新表情面板的自定义表情列表
				const chatOverlays = this.getChatOverlaysRef();
				if (chatOverlays && chatOverlays.refreshCustomStickers) {
					chatOverlays.refreshCustomStickers();
				}
			})
		},
		onDownloadFile(msgInfo) {
			const url = JSON.parse(msgInfo.content).url;
			const name = JSON.parse(msgInfo.content).name;
			this.$assetResolver.download(url, {
				fileName: name
			}).catch((error) => {
				this.$message.error(error && error.message ? error.message : '文件下载失败');
			});
		},
		// 批量删除消息
		onBatchDelete() {
			const selectedMessageState = this.resolveSelectedMessageState();
			if (selectedMessageState.count === 0) {
				this.$message.warning('请先选择要删除的消息');
				return;
			}
			this.$confirm(`确认删除选中的 ${selectedMessageState.count} 条消息?`, '批量删除消息', {
				confirmButtonText: '确定',
				cancelButtonText: '取消',
				type: 'warning'
			}).then(async () => {
				const ids = selectedMessageState.deleteIds;
				if (ids.length > 0) {
					const data = {
						chatId: this.chat.targetId,
						messageIds: ids
					}
					await this.$http({
						url: `/message/${this.chat.type.toLowerCase()}/deleteMessage`,
						method: 'delete',
						data: data
					});
				}
				this.chatStore.deleteMessages(this.selectedMessages, this.chat);
				this.chatStore.refreshLastContent(this.chat);
				this.exitMultiSelectMode();
				this.$message.success('删除成功');
			});
		},
		// 批量转发消息（逐条转发）
		onBatchForward() {
			if (!this.$permission.hasRolePermission('message.forward')) {
				this.$message.error('您没有消息转发权限');
				return;
			}
			const selectedMessageState = this.resolveSelectedMessageState();
			if (selectedMessageState.count === 0) {
				this.$message.warning('请先选择要转发的消息');
				return;
			}
			const forwardableMessages = selectedMessageState.batchForwardableMessages;
			// 如果有被过滤的消息，提示用户
			if (forwardableMessages.length < selectedMessageState.count) {
				this.$confirm('你选择的消息中, 名片消息、通话消息以及未发送成功的消息将不能转发,是否继续?', '提示', {
					confirmButtonText: '继续发送',
					cancelButtonText: '取消',
					type: 'warning'
				}).then(() => {
					// 用户确认后继续转发
					this.doBatchForward(forwardableMessages);
				})
				return;
			}
			this.doBatchForward(forwardableMessages);
		},
		// 执行批量转发
		async doBatchForward(forwardableMessages) {
			if (forwardableMessages.length === 0) {
				this.$message.warning('没有可转发的消息');
				return;
			}
			const chatOverlays = this.getChatOverlaysRef();
			if (!chatOverlays || !chatOverlays.openChatSelector) {
				return;
			}
			chatOverlays.openChatSelector(async (chats) => {
				this.exitMultiSelectMode();
				await this.sendForwardMessages(chats, forwardableMessages, {
					successText: `成功转发 ${forwardableMessages.length} 条消息到 ${chats.length} 个会话`,
					failText: '转发失败'
				});
			});
		},
		// 合并转发消息
		onMergeForward() {
			if (!this.$permission.hasRolePermission('message.forward')) {
				this.$message.error('您没有消息转发权限');
				return;
			}
			const selectedMessageState = this.resolveSelectedMessageState();
			if (selectedMessageState.count < 2) {
				this.$message.warning('合并转发至少需要选择2条消息');
				return;
			}
			const canForwardCard = this.canForwardCardMessages();
			const forwardableMessages = selectedMessageState.sentMessages
				.filter(message => !this.isCardMessageType(message.type) || canForwardCard)
				.sort((a, b) => a.id - b.id);
			const blockedCount = selectedMessageState.count - forwardableMessages.length;
			// 如果有未发送成功或无权限转发的消息，提示用户
			if (blockedCount > 0) {
				this.$confirm(`你选择的消息中，有 ${blockedCount} 条未发送成功或无权限转发的消息不能转发，是否继续转发其他消息？`, '提示', {
					confirmButtonText: '继续转发',
					cancelButtonText: '取消',
					type: 'warning'
				}).then(() => {
					// 用户确认后继续转发
					this.doMergeForward(forwardableMessages);
				})
				return;
			}
			this.doMergeForward(forwardableMessages);
		},
		// 执行合并转发
		doMergeForward(forwardableMessages) {
			if (forwardableMessages.length === 0) {
				this.$message.warning('没有可转发的消息');
				return;
			}
			let title = "聊天记录";
			if (this.isGroup) {
				title = "群聊'" + this.group.name + "'的聊天记录"
			} else {
				title = `'${this.chat.showName}'和'${this.mine.nickName}'的聊天记录`
			}
			// 构建合并转发的数据
			const mergeData = {
				messages: forwardableMessages.map(msg => {
					let message = {
						id: msg.id,
						type: msg.type,
						sendId: msg.sendId,
						sendNickName: this.showName(msg),
						sendHeadImage: this.headImage(msg),
						sendTime: msg.sendTime,
						content: msg.content
					}
					if (msg.type == this.$enums.MESSAGE_TYPE.MERGE_FORWARD) {
						// 合并转发里面又嵌套了合并转发内容，清空消息体避免报文体积过大
						const mergeData = JSON.parse(msg.content)
						mergeData.messages = mergeData.messages.map(m => m.id);
						message.content = JSON.stringify(mergeData);
					}
					return message;
				}),
				title: title,
				chatType: this.chat.type
			};
			// 用户确认后，打开会话选择器
			const chatOverlays = this.getChatOverlaysRef();
			if (!chatOverlays || !chatOverlays.openChatSelector) {
				return;
			}
			chatOverlays.openChatSelector(async (chats) => {
				this.exitMultiSelectMode();
				await this.sendForwardMessages(chats, [{
					content: JSON.stringify(mergeData),
					type: this.$enums.MESSAGE_TYPE.MERGE_FORWARD
				}], {
					successText: `成功合并转发 ${forwardableMessages.length} 条消息到 ${chats.length} 个会话`,
					failText: '合并转发失败'
				});
			});
		},
		onRecallMessage(msgInfo) {
			this.$confirm('确认撤回消息?', '撤回消息', {
				confirmButtonText: '确定',
				cancelButtonText: '取消',
				type: 'warning'
			}).then(() => {
				let url = `/message/${this.chat.type.toLowerCase()}/recall/${msgInfo.id}`
				this.$http({
					url: url,
					method: 'delete'
				}).then((m) => {
					this.$message.success("消息已撤回");
					m.selfSend = true;
					this.chatStore.recallMessage(m, this.chat);
				})
			});
		},
		onEditMessage({ msgInfo, newContent }) {
			// 发送编辑请求到后端
			let url = `/message/${this.chat.type.toLowerCase()}/edit`;
			this.$http({
				url: url,
				method: 'put',
				data: {
					messageId: msgInfo.id,
					content: newContent
				}
			}).then((m) => {
				this.$message.success("消息已编辑");
				// 更新本地消息
				m.selfSend = true;
				this.chatStore.updateMessage(m, this.chat);
			}).catch((error) => {
				this.$message.error(error.message || "消息编辑失败");
			});
		},
		onQuoteMessage(msgInfo) {
			this.quoteMessage = msgInfo;
			if (!msgInfo) {
				return;
			}
			this.$nextTick(() => {
				const chatFooter = this.getChatFooterRef();
				if (chatFooter && chatFooter.focusInput) {
					chatFooter.focusInput();
				}
			});
		},
		onForwardMessage(msgInfo) {
			if (!this.$permission.hasRolePermission('message.forward')) {
				this.$message.error('您没有消息转发权限');
				return;
			}
			if (this.isCardMessageType(msgInfo.type) && !this.canForwardCardMessages()) {
				this.$message.error('您没有名片转发权限');
				return;
			}
			const chatOverlays = this.getChatOverlaysRef();
			if (!chatOverlays || !chatOverlays.openChatSelector) {
				return;
			}
			chatOverlays.openChatSelector(async (chats) => {
				await this.sendForwardMessages(chats, [{
					content: msgInfo.content,
					type: msgInfo.type
				}], {
					successText: '转发成功',
					failText: '转发失败'
				});
			});
		},
		getGroupTopMessages(groupId) {
			const group = this.groupStore.findGroup(groupId);
			return normalizeTopMessages(group && group.topMessages);
		},
		syncLocalTopMessageList(groupId) {
			if (!this.activeGroupId || String(groupId) !== String(this.activeGroupId)) {
				return;
			}
			this.topMessageList = this.getGroupTopMessages(groupId);
		},
		applyLocalTopMessageAdded(msgInfo) {
			if (!msgInfo || !msgInfo.groupId || msgInfo.id === undefined || msgInfo.id === null) {
				return;
			}
			this.groupStore.updateTopMessage(msgInfo.groupId, msgInfo);
			this.syncLocalTopMessageList(msgInfo.groupId);
		},
		applyLocalTopMessageRemoved(groupId, removedIds = []) {
			if (!groupId) {
				return;
			}
			const nextTopMessages = removeTopMessages(this.getGroupTopMessages(groupId), removedIds);
			const group = this.groupStore.findGroup(groupId);
			if (!group) {
				return;
			}
			if (!Array.isArray(group.topMessages)) {
				group.topMessages = [];
			}
			group.topMessages.splice(0, group.topMessages.length, ...nextTopMessages);
			this.syncLocalTopMessageList(groupId);
		},
		onTopMessage(msgInfo) {
			const topMessages = this.getGroupTopMessages(msgInfo.groupId);
			// 判断当前消息是否已置顶
			const isTopped = topMessages.some((message) => message.id == msgInfo.id);

			if (isTopped) {
				// 取消置顶
				this.$http({
					url: `/group/removeTopMessage/${msgInfo.groupId}?messageId=${msgInfo.id}`,
					method: 'delete'
				}).then(() => {
					this.applyLocalTopMessageRemoved(msgInfo.groupId, [msgInfo.id]);
					const { expectedTopCount, expectedLatestId } = getTopMessageExpectation(this.getGroupTopMessages(msgInfo.groupId));
					this.$message.success("已取消置顶");
					this.syncGroupInfo(msgInfo.groupId, {
						expectedTopCount,
						expectedLatestId,
						retryTimes: 2,
						trackTopMessageSync: true,
						refreshMembers: true,
						refreshHistory: true
					});
				})
			} else {
				// 置顶
				this.$http({
					url: `/group/setTopMessage/${msgInfo.groupId}?messageId=${msgInfo.id}`,
					method: 'post'
				}).then(() => {
					this.applyLocalTopMessageAdded(msgInfo);
					const { expectedTopCount, expectedLatestId } = getTopMessageExpectation(this.getGroupTopMessages(msgInfo.groupId));
					this.$message.success("消息已置顶");
					this.syncGroupInfo(msgInfo.groupId, {
						expectedTopCount,
						expectedLatestId,
						retryTimes: 2,
						trackTopMessageSync: true,
						refreshMembers: true,
						refreshHistory: true
					});
				}).catch((error) => {
					const message = error && error.message ? error.message : '消息置顶失败';
					this.$message.warning(message);
					this.syncGroupInfo(msgInfo.groupId, {
						retryTimes: 1,
						trackTopMessageSync: true,
						refreshMembers: true,
						refreshHistory: false
					});
				})
			}
		},
		openTopMessageList() {
			if (!this.activeGroupId) {
				return;
			}
			this.showTopMessageDialog = true;
			this.loadTopMessageList();
		},
		loadTopMessageList() {
			const groupId = this.activeGroupId;
			if (!groupId) {
				return;
			}
			this.topMessageLoading = true;
			this.$http({
				url: `/group/find/${groupId}`,
				method: 'get'
			}).then((groupInfo) => {
				this.topMessageList = normalizeTopMessages(groupInfo.topMessages);
				groupInfo.topMessages = this.topMessageList;
				this.groupStore.upsertGroup(groupInfo);
			}).catch(() => {
				this.topMessageList = [];
			}).finally(() => {
				this.topMessageLoading = false;
			});
		},
		onSyncTopMessageState(payload = {}) {
			const groupId = this.activeGroupId;
			if (!groupId) {
				return;
			}
			const action = payload.action || '';
			if (action === 'remove') {
				this.applyLocalTopMessageRemoved(groupId, [payload.msgInfo && payload.msgInfo.id]);
			} else if (action === 'remove-all') {
				const removedIds = Array.isArray(payload.removedIds) && payload.removedIds.length
					? payload.removedIds
					: this.getGroupTopMessages(groupId).map((item) => item.id);
				this.applyLocalTopMessageRemoved(groupId, removedIds);
			}
			const { expectedTopCount, expectedLatestId } = getTopMessageExpectation(this.getGroupTopMessages(groupId));
			this.syncGroupInfo(groupId, {
				expectedTopCount,
				expectedLatestId,
				retryTimes: 2,
				trackTopMessageSync: true,
				refreshMembers: true,
				refreshHistory: true,
				reloadTopDialog: this.showTopMessageDialog
			});
		},
		onRemoveTopMessageFromDialog(msgInfo) {
			if (!msgInfo || this.removingTopMessageId) {
				return;
			}
			const groupId = this.activeGroupId;
			if (!groupId) {
				return;
			}
			this.$confirm('仅取消当前这条置顶消息，确认继续？', '取消置顶', {
				confirmButtonText: '移除',
				cancelButtonText: '取消',
				type: 'warning'
			}).then(() => {
				this.removingTopMessageId = msgInfo.id;
				return this.$http({
					url: `/group/removeTopMessage/${groupId}?messageId=${msgInfo.id}`,
					method: 'delete'
				});
			}).then(() => {
				this.applyLocalTopMessageRemoved(groupId, [msgInfo.id]);
				const { expectedTopCount, expectedLatestId } = getTopMessageExpectation(this.getGroupTopMessages(groupId));
				this.$message.success('已取消置顶');
				this.syncGroupInfo(groupId, {
					expectedTopCount,
					expectedLatestId,
					retryTimes: 2,
					trackTopMessageSync: true,
					refreshMembers: true,
					refreshHistory: true,
					reloadTopDialog: true
				});
			}).catch((error) => {
				if (error !== 'cancel' && error !== 'close') {
					this.$message.error('取消置顶失败，请重试');
				}
			}).finally(() => {
				this.removingTopMessageId = null;
			});
		},
		onLocateTopMessage(msgInfo) {
			this.showTopMessageDialog = false;
			this.locateMessage(msgInfo);
		},
		onLocateQuoteMessage(msgInfo) {
			this.locateMessage(msgInfo.quoteMessage);
		},
		locateMessage(msgInfo) {
			// 寻找消息位置
			const idx = this.findMessageIdx(msgInfo);
			if (idx < 0) {
				this.$message.error("无法定位原消息");
				return;
			}
			// 要定位到消息，首先要渲染这条消息
			const contextSize = MESSAGE_RENDER_BATCH_SIZE;
			let contextMinIdx = Math.max(Number(idx) - Math.floor(contextSize / 2), 0);
			let contextMaxIdx = Math.min(contextMinIdx + contextSize, this.messageSize);
			contextMinIdx = Math.max(contextMaxIdx - contextSize, 0);
			this.setMessageRenderWindow(contextMinIdx, contextMaxIdx);
			// 定位消息
			this.scrollToMessage(msgInfo.id, 100, 0);
			// 选中消息
			this.activeMessageIdx = idx;
			// 设置底部标记
			if (this.showMaxIdx > 0 && this.showMaxIdx < this.messageSize) {
				// 延时是为了在滚动事件触发之后设置
				setTimeout(() => {
					this.isInBottom = false;
					this.newMessageSize = 0;
				}, 100)
			}
			const chatOverlays = this.getChatOverlaysRef();
			if (chatOverlays && chatOverlays.closeHistory) {
				chatOverlays.closeHistory();
			}
		},
		scrollToMessage(id, delay, times) {
			setTimeout(() => {
				let messgaeItem = document.getElementById(id);
				if (messgaeItem) {
					messgaeItem.scrollIntoView({ behavior: 'smooth' });
				} else if (times < 3) {
					this.scrollToMessage(id, delay * 3, times + 1)
				} else {
					console.log("消息定位失败", delay)
				}
			}, delay)
		},
		scrollToAtMessage() {
			this.locateMessage({ id: this.chat.lastAtMessageId });
			this.chatStore.resetAtMessage(this.chat);
		},
		resetShowMessages() {
			this.showMinIdx = this.messageSize > MESSAGE_INITIAL_RENDER_SIZE
				? this.messageSize - MESSAGE_INITIAL_RENDER_SIZE
				: 0;
			this.showMaxIdx = -1;
			this.refreshAtMessage();
		},
		resolveMessageWindowEnd() {
			if (this.showMaxIdx > 0) {
				return Math.min(this.showMaxIdx, this.messageSize);
			}
			return this.messageSize;
		},
		setMessageRenderWindow(minIdx, maxIdx) {
			if (this.messageSize <= 0) {
				this.showMinIdx = 0;
				this.showMaxIdx = -1;
				return;
			}
			const safeMaxIdx = Math.max(0, Math.min(maxIdx, this.messageSize));
			const safeMinIdx = Math.max(0, Math.min(minIdx, safeMaxIdx));
			this.showMinIdx = safeMinIdx;
			this.showMaxIdx = safeMaxIdx >= this.messageSize ? -1 : safeMaxIdx;
		},
		expandMessageWindowAtTop() {
			if (this.messageSize <= 0) {
				return;
			}
			const currentMaxIdx = this.resolveMessageWindowEnd();
			const nextMinIdx = Math.max(this.showMinIdx - MESSAGE_RENDER_BATCH_SIZE, 0);
			let nextMaxIdx = currentMaxIdx;
			if (nextMaxIdx - nextMinIdx > MESSAGE_RENDER_WINDOW_SIZE) {
				nextMaxIdx = Math.min(this.messageSize, nextMinIdx + MESSAGE_RENDER_WINDOW_SIZE);
			}
			this.setMessageRenderWindow(nextMinIdx, nextMaxIdx);
		},
		expandMessageWindowAtBottom() {
			if (this.messageSize <= 0 || this.showMaxIdx < 0) {
				return false;
			}
			const previousMinIdx = this.showMinIdx;
			const previousMaxIdx = this.showMaxIdx;
			const nextMaxIdx = Math.min(this.resolveMessageWindowEnd() + MESSAGE_RENDER_BATCH_SIZE, this.messageSize);
			let nextMinIdx = this.showMinIdx;
			if (nextMaxIdx - nextMinIdx > MESSAGE_RENDER_WINDOW_SIZE) {
				nextMinIdx = Math.max(nextMaxIdx - MESSAGE_RENDER_WINDOW_SIZE, 0);
			}
			this.setMessageRenderWindow(nextMinIdx, nextMaxIdx);
			return previousMinIdx !== this.showMinIdx || previousMaxIdx !== this.showMaxIdx;
		},
		trimMessageWindowFromTop() {
			const currentMaxIdx = this.resolveMessageWindowEnd();
			if (currentMaxIdx - this.showMinIdx <= MESSAGE_RENDER_WINDOW_SIZE) {
				return;
			}
			this.setMessageRenderWindow(this.showMinIdx, this.showMinIdx + MESSAGE_RENDER_WINDOW_SIZE);
		},
		captureMessageScrollAnchor(scrollElement) {
			if (!scrollElement) {
				return null;
			}
			const scrollRect = scrollElement.getBoundingClientRect();
			const items = Array.from(scrollElement.querySelectorAll('.chat-message-item[id]'));
			const anchorElement = items.find((item) => {
				const rect = item.getBoundingClientRect();
				return rect.bottom >= scrollRect.top;
			});
			if (!anchorElement) {
				return null;
			}
			const rect = anchorElement.getBoundingClientRect();
			return {
				id: anchorElement.id,
				top: rect.top - scrollRect.top
			};
		},
		restoreMessageScrollAnchor(scrollElement, anchor) {
			if (!scrollElement || !anchor || !anchor.id) {
				return false;
			}
			const anchorElement = document.getElementById(anchor.id);
			if (!anchorElement) {
				return false;
			}
			const scrollRect = scrollElement.getBoundingClientRect();
			const rect = anchorElement.getBoundingClientRect();
			scrollElement.scrollTop += rect.top - scrollRect.top - anchor.top;
			return true;
		},
		restoreMessageBottomDistance(scrollElement, distanceFromBottom = 0) {
			if (!scrollElement) {
				return;
			}
			const safeDistance = Math.max(0, Number(distanceFromBottom) || 0);
			const maxScrollTop = Math.max(scrollElement.scrollHeight - scrollElement.clientHeight, 0);
			const targetScrollTop = Math.max(0, maxScrollTop - safeDistance);
			scrollElement.scrollTop = targetScrollTop;
		},
		beginBottomWindowLoading() {
			const loadingToken = this.bottomWindowLoadingToken + 1;
			this.bottomWindowLoadingToken = loadingToken;
			this.bottomWindowLoadingVisible = true;
			return loadingToken;
		},
		finishBottomWindowLoading(loadingToken, loadingStartedAt) {
			const hideLoading = () => {
				if (this.bottomWindowLoadingToken === loadingToken) {
					this.bottomWindowLoadingVisible = false;
				}
			};
			const elapsed = Date.now() - loadingStartedAt;
			const delay = Math.max(BOTTOM_WINDOW_LOADING_MIN_VISIBLE_MS - elapsed, 0);
			if (delay > 0) {
				setTimeout(hideLoading, delay);
				return;
			}
			hideLoading();
		},
		refreshAtMessage() {
			// @我的消息已读，需要清除“有人@我”标志
			if (this.chat.atMe || this.chat.atAll) {
				const idx = this.findMessageIdx({ id: this.chat.lastAtMessageId });
				if (this.showMinIdx < idx) {
					this.chatStore.resetAtMessage(this.chat);
				}
			}
		},
		findMessageIdx(msgInfo) {
			for (let idx in this.messageList) {
				const message = this.messageList[idx];
				// 通过id判断
				if (msgInfo.id && message.id && message.id == msgInfo.id) {
					return idx;
				}
				// 正在发送中的消息可能没有id,只有tmpId
				if (msgInfo.tmpId && message.tmpId && message.tmpId == msgInfo.tmpId) {
					return idx;
				}
			}
			return -1;
		},
		readedMessage() {
			if (this.chat.unreadCount <= 0 || this.readRequest) return;
			const readChat = this.chat;
			const messageId = (readChat.messages || []).reduce((maxId, message) =>
				message && message.id ? Math.max(maxId, Number(message.id) || 0) : maxId, 0);
			if (!messageId) return;
			const path = this.isGroup ? 'group' : 'private';
			const targetParam = this.isGroup ? `groupId=${readChat.targetId}` : `friendId=${readChat.targetId}`;
			this.readRequest = this.$http({
				url: `/message/${path}/readed?${targetParam}&messageId=${messageId}`,
				method: 'put'
			}).then(() => {
				this.chatStore.advanceReadCursor(readChat, messageId);
				const latestMessageId = (readChat.messages || []).reduce((maxId, message) =>
					message && message.id ? Math.max(maxId, Number(message.id) || 0) : maxId, 0);
				if (latestMessageId <= messageId) this.chatStore.resetUnreadCount(readChat);
			}).finally(() => {
				this.readRequest = null;
			});
		},
		loadReaded(fId) {
			this.$http({
				url: `/message/private/maxReadedId?friendId=${fId}`,
				method: 'get'
			}).then((id) => {
				this.chatStore.readedMessage({
					friendId: fId,
					maxId: id
				});
			});
		},
		syncGroupInfo(groupId, options = {}) {
			if (!groupId) {
				return Promise.resolve(null);
			}
			const {
				expectedTopCount,
				expectedLatestId,
				retryTimes = 0,
				retryDelay = 350,
				refreshMembers = false,
				refreshHistory = false,
				refreshOnline = false,
				trackTopMessageSync = false,
				reloadTopDialog = false,
				requestToken = this.groupInfoRequestToken + 1
			} = options;
			if (requestToken > this.groupInfoRequestToken) {
				this.groupInfoRequestToken = requestToken;
			}
			const isCurrentRequest = () => requestToken === this.groupInfoRequestToken &&
				this.isActiveGroupChat(groupId);
			if (isCurrentRequest()) {
				this.groupInfoLoading = true;
				this.groupInfoError = false;
				this.groupInfoErrorMessage = '';
			}
			if (trackTopMessageSync && String(groupId) === String(this.activeGroupId)) {
				this.topMessageSyncing = true;
			}
			return this.groupStore.fetchGroupInfo(groupId).then((group) => {
				if (!isCurrentRequest()) {
					return null;
				}
				if (!group || group.id === undefined || group.id === null || group.id === '') {
					throw new Error('群资料响应为空');
				}
				const resolvedTopState = resolveTopMessageSyncState({
					localTopMessages: this.getGroupTopMessages(groupId),
					incomingTopMessages: group.topMessages,
					expectedTopCount,
					expectedLatestId,
					preserveOnMismatch: retryTimes > 0
				});
				group.topMessages = resolvedTopState.topMessages;
				this.groupStore.upsertGroup(group);
				this.chatStore.updateChatFromGroup(group);
				if (isUnavailableGroup(group)) {
					this.removeUnavailableGroupConversation(groupId, group);
					return null;
				}
				if (reloadTopDialog && String(groupId) === String(this.activeGroupId)) {
					this.topMessageList = normalizeTopMessages(group.topMessages);
				}
				if (refreshMembers) {
					this.groupStore.refreshMember(groupId, {
						refreshOnline,
						pageLimit: 30
					});
				} else if (refreshOnline) {
					this.refreshGroupOnlineCount(groupId);
				}
				if (refreshHistory) {
					this.loadChatHistory({
						type: 'GROUP',
						targetId: groupId
					});
				}
				if (!resolvedTopState.matched && retryTimes > 0) {
					return new Promise((resolve) => {
						setTimeout(() => resolve(this.syncGroupInfo(groupId, {
							expectedTopCount,
							expectedLatestId,
							retryTimes: retryTimes - 1,
							retryDelay,
							refreshMembers,
							refreshHistory,
							refreshOnline,
							trackTopMessageSync,
							reloadTopDialog,
							requestToken
						})), retryDelay);
					});
				}
				if (trackTopMessageSync && String(groupId) === String(this.activeGroupId)) {
					this.topMessageSyncing = false;
				}
				return group;
			}).catch((error) => {
				if (!isCurrentRequest()) {
					return null;
				}
				if (isUnavailableGroupError(error)) {
					this.removeUnavailableGroupConversation(groupId, null, error);
					return null;
				}
				if ((typeof expectedTopCount === 'number' || expectedLatestId !== undefined) && retryTimes > 0) {
					return new Promise((resolve) => {
						setTimeout(() => resolve(this.syncGroupInfo(groupId, {
							expectedTopCount,
							expectedLatestId,
							retryTimes: retryTimes - 1,
							retryDelay,
							refreshMembers,
							refreshHistory,
							refreshOnline,
							trackTopMessageSync,
							reloadTopDialog,
							requestToken
						})), retryDelay);
					});
				}
				if (trackTopMessageSync && String(groupId) === String(this.activeGroupId)) {
					this.topMessageSyncing = false;
				}
				if (refreshHistory) this.loadChatHistory({ type: 'GROUP', targetId: groupId });
				this.groupInfoError = true;
				this.groupInfoErrorMessage = '群资料读取失败，暂不能确认会话状态';
				console.warn('群资料读取失败', { groupId, error });
				return null;
			}).finally(() => {
				if (isCurrentRequest()) {
					this.groupInfoLoading = false;
				}
			});
		},
		removeUnavailableGroupConversation(groupId, group = null, error = null) {
			const chat = this.chatStore.findChat({
				type: 'GROUP',
				targetId: groupId
			});
			if (group) {
				this.groupStore.upsertGroup(group);
			} else {
				this.groupStore.removeGroup(groupId);
			}
			this.groupInfoError = false;
			this.groupInfoErrorMessage = '';
			this.showSide = false;
			if (chat) {
				this.chatStore.removeGroupChat(groupId);
				this.$message.warning(resolveUnavailableGroupNotice(group, error));
			}
		},
		retryGroupInfo() {
			const groupId = this.activeGroupId;
			if (!groupId || this.groupInfoLoading) {
				return Promise.resolve(null);
			}
			return this.loadGroup(groupId, {
				refreshMembers: false,
				refreshHistory: true,
				refreshOnline: false
			});
		},
		loadGroup(groupId, options = {}) {
			if (!groupId) {
				return Promise.resolve(null);
			}
			this.groupId = groupId;
			const {
				refreshMembers = true,
				refreshHistory = true,
				refreshOnline = false
			} = options;
			return this.syncGroupInfo(groupId, {
				refreshMembers,
				refreshHistory,
				refreshOnline
			});
		},
		loadNewGroupAfterFirstPaint(groupId) {
			if (!groupId) {
				return;
			}
			this.groupId = groupId;
			this.runAfterPaint(() => {
				if (!this.isActiveGroupChat(groupId)) {
					return;
				}
				this.runWhenBrowserIdle(() => {
					if (!this.isActiveGroupChat(groupId)) {
						return;
					}
					const group = this.groupStore.findGroup(groupId);
					if (!group || !group.id) {
						this.loadGroup(groupId, {
							refreshMembers: false,
							refreshHistory: false
						});
					}
				});
			});
		},
		resolveHistoryChatKey(chatInfo = this.chat) {
			if (!chatInfo || !chatInfo.type || !chatInfo.targetId) {
				return '';
			}
			return `${chatInfo.type}:${chatInfo.targetId}`;
		},
		loadOlderHistoryFromScroll(scrollElement) {
			const activeChat = this.chat;
			if (!activeChat || !activeChat.targetId || !scrollElement) {
				return;
			}
			const beforeScrollHeight = scrollElement.scrollHeight;
			const beforeScrollTop = scrollElement.scrollTop;
			const scrollAnchor = this.captureMessageScrollAnchor(scrollElement);
			this.loadChatHistory(activeChat, {
				older: true
			}).then((insertedCount) => {
				if (!insertedCount || !this.chat || this.chat.type != activeChat.type ||
					this.chat.targetId != activeChat.targetId) {
					return;
				}
				this.$nextTick(() => {
					if (this.restoreMessageScrollAnchor(scrollElement, scrollAnchor)) {
						return;
					}
					const heightDelta = scrollElement.scrollHeight - beforeScrollHeight;
					if (heightDelta > 0) {
						scrollElement.scrollTop = beforeScrollTop + heightDelta;
					}
				});
			});
		},
		retryLoadOlderHistory() {
			this.historyError = false;
			const chat = this.chatStore.findChat(this.chat);
			if (!this.resolveLoadedHistoryState(chat).realMessageCount) {
				return this.loadChatHistory(this.chat);
			}
			const viewport = this.$refs.messageViewport;
			const scrollElement = viewport && viewport.getScrollElement
				? viewport.getScrollElement()
				: document.getElementById('chatScrollBox');
			if (scrollElement) {
				this.loadOlderHistoryFromScroll(scrollElement);
			}
		},
		loadChatHistory(chatInfo = this.chat, options = {}) {
			const chat = this.chatStore.findChat(chatInfo);
			const loadedHistoryState = this.resolveLoadedHistoryState(chat);
			const loadOlder = options.older === true;
			if (!chat || (!loadOlder && loadedHistoryState.realMessageCount > 0)) {
				return Promise.resolve(0);
			}
			const historyCutoffSeqNo = Number(chat && chat.historyCutoffSeqNo || 0) || 0;
			const historyChatKey = this.resolveHistoryChatKey(chat);
			if (this.historyChatKey !== historyChatKey) {
				this.historyChatKey = historyChatKey;
				this.historyLoadingToken++;
				if (this.historyLoadingTimer) {
					clearTimeout(this.historyLoadingTimer);
					this.historyLoadingTimer = null;
				}
				this.historyFinished = false;
				this.historyLoading = false;
				this.historyLoadingVisible = false;
				this.historyError = false;
				this.historyCursor = '';
			}
			if (this.historyLoading || (loadOlder && this.historyFinished)) {
				return Promise.resolve(0);
			}
			const maxSeqNo = loadOlder ? loadedHistoryState.minSeqNo : 0;
			if (loadOlder && !maxSeqNo) {
				return Promise.resolve(0);
			}
			this.historyLoading = true;
			this.historyLoadingVisible = false;
			this.historyLoadingVisibleAt = 0;
			this.historyError = false;
			const loadingToken = this.historyLoadingToken + 1;
			this.historyLoadingToken = loadingToken;
			const isCurrentHistoryLoad = () => this.historyLoadingToken === loadingToken
				&& this.historyChatKey === historyChatKey;
			const loadingTimer = setTimeout(() => {
				if (this.historyLoadingToken === loadingToken && this.historyLoading) {
					this.historyLoadingVisible = true;
					this.historyLoadingVisibleAt = Date.now();
				}
				if (this.historyLoadingTimer === loadingTimer) {
					this.historyLoadingTimer = null;
				}
			}, HISTORY_LOADING_SHOW_DELAY_MS);
			this.historyLoadingTimer = loadingTimer;
			const convKey = this.$db && typeof this.$db.buildConversationKey === 'function'
				? this.$db.buildConversationKey(chat.type, chat.targetId)
				: `${chat.type}-${chat.targetId}`;
			const loadLocalMessages = () => {
				if (!this.$db || chat.type == 'SYSTEM') {
					return Promise.resolve([]);
				}
				if (loadOlder) {
					return this.$db.findMessagesBefore(convKey, maxSeqNo, GROUP_HISTORY_PAGE_SIZE);
				}
				return this.$db.findMessagesBefore(convKey, Number.MAX_SAFE_INTEGER, GROUP_HISTORY_PAGE_SIZE);
			};
			const loadRemoteMessages = (options = {}) => {
				const pageSize = Math.max(1, options.limit || GROUP_HISTORY_PAGE_SIZE);
				const query = [`limit=${pageSize}`];
				let url = '';
				if (chat.type == 'GROUP') {
					query.unshift(`groupId=${chat.targetId}`);
					url = '/message/group/history';
				} else if (chat.type == 'PRIVATE') {
					query.unshift(`friendId=${chat.targetId}`);
					url = '/message/private/history';
				} else {
					return Promise.resolve([]);
				}
				if (options.cursor) {
					query.push(`cursor=${encodeURIComponent(String(options.cursor))}`);
				}
				return this.$http({
					url: `${url}?${query.join('&')}`,
					method: 'get'
				}).then((payload) => {
					const page = payload && typeof payload === 'object' ? payload : {};
					const items = Array.isArray(page.items) ? page.items : [];
					const more = Boolean(page.more && page.cursor);
					return {
						items,
						cursor: more ? String(page.cursor) : '',
						more
					};
				});
			};
			return Promise.resolve().then(loadLocalMessages).catch((error) => {
				console.warn('本地历史读取失败，改为读取服务端历史', error);
				return [];
			}).then((localMessages) => {
				if (!isCurrentHistoryLoad()) {
					return 0;
				}
				const visibleLocalMessages = this.filterVisibleHistoryMessages(localMessages, historyCutoffSeqNo);
				const targetChat = this.chatStore.findChat(chat);
				const markRecentHistoryHydrated = () => {
					if (loadOlder || !targetChat || targetChat.recentHistoryHydrated) {
						return false;
					}
					targetChat.recentHistoryHydrated = true;
					this.chatStore.markChatDirty(targetChat);
					return true;
				};
				const persistHydrationFlagIfNeeded = (changed, insertedCount) => {
					if (changed && insertedCount === 0) {
						this.chatStore.saveToStorageInTimer();
					}
				};
				const flushInitialHistoryHydration = () => {
					if (!loadOlder) {
						this.chatStore.saveToStorage();
					}
				};
				const insertMessages = (messages, source = 'remote') => {
					if (!targetChat || !Array.isArray(messages) || messages.length === 0) {
						return 0;
					}
					messages.forEach((msg) => {
						msg.selfSend = msg.sendId == this.mine.id;
					});
					if (loadOlder) {
						this.suppressMessageGrowthScroll = true;
					}
					const insertedCount = this.chatStore.mergeHistoryMessages(messages, targetChat, { source });
					if (loadOlder && insertedCount > 0) {
						this.trimMessageWindowFromTop();
					}
					if (!loadOlder && insertedCount > 0) {
						this.$nextTick(() => {
							if (isCurrentHistoryLoad() && this.chatStore.findChat(chat)) {
								this.scrollToBottom();
							}
						});
					}
					return insertedCount;
				};
				if (visibleLocalMessages.length > 0) {
					if (!loadOlder) {
						const localInsertedCount = insertMessages(visibleLocalMessages, 'local-db-page');
						this.historyFinished = false;
						if (visibleLocalMessages.length >= GROUP_HISTORY_PAGE_SIZE) {
							return localInsertedCount;
						}
						if (targetChat && targetChat.recentHistoryHydrated) {
							this.historyFinished = true;
							return localInsertedCount;
						}
						return loadRemoteMessages({
							limit: GROUP_HISTORY_PAGE_SIZE
						}).then((page) => {
							if (!isCurrentHistoryLoad()) return localInsertedCount;
							const remoteMessages = this.filterVisibleHistoryMessages(page.items, historyCutoffSeqNo);
							const hydrationChanged = markRecentHistoryHydrated();
							const remoteInsertedCount = insertMessages(remoteMessages);
							this.historyCursor = page.cursor;
							if (!page.more) {
								this.historyFinished = true;
							}
							persistHydrationFlagIfNeeded(hydrationChanged, remoteInsertedCount);
							flushInitialHistoryHydration();
							return localInsertedCount + remoteInsertedCount;
						});
					}
					const localInsertedCount = insertMessages(visibleLocalMessages, 'local-db-page');
					if (visibleLocalMessages.length >= GROUP_HISTORY_PAGE_SIZE) {
						return localInsertedCount;
					}
					const remainingSize = GROUP_HISTORY_PAGE_SIZE - visibleLocalMessages.length;
					return loadRemoteMessages({
						cursor: this.historyCursor,
						limit: remainingSize
					}).then((page) => {
						if (!isCurrentHistoryLoad()) return localInsertedCount;
						const remoteMessages = this.filterVisibleHistoryMessages(page.items, historyCutoffSeqNo);
						const remoteInsertedCount = insertMessages(remoteMessages);
						this.historyCursor = page.cursor;
						if (!page.more) {
							this.historyFinished = true;
						}
						return localInsertedCount + remoteInsertedCount;
					});
				}
				if (!loadOlder && historyCutoffSeqNo > 0) {
					this.historyFinished = true;
					return 0;
				}
				return loadRemoteMessages({
					cursor: loadOlder ? this.historyCursor : '',
					limit: GROUP_HISTORY_PAGE_SIZE
				}).then((page) => {
					if (!isCurrentHistoryLoad()) return 0;
					const remoteMessages = this.filterVisibleHistoryMessages(page.items, historyCutoffSeqNo);
					if (!loadOlder) {
						this.historyFinished = false;
					}
					const hydrationChanged = markRecentHistoryHydrated();
					const insertedCount = insertMessages(remoteMessages);
					this.historyCursor = page.cursor;
					if (!loadOlder && !page.more) {
						this.historyFinished = true;
					}
					if (loadOlder && !page.more) {
						this.historyFinished = true;
					}
					persistHydrationFlagIfNeeded(hydrationChanged, insertedCount);
					flushInitialHistoryHydration();
					return insertedCount;
				});
			}).catch((error) => {
				if (isCurrentHistoryLoad()) {
					this.historyError = true;
					console.warn('加载聊天记录失败', error);
				}
				return 0;
			}).finally(() => {
				if (!isCurrentHistoryLoad()) {
					return;
				}
				this.historyLoading = false;
				if (this.historyLoadingTimer === loadingTimer) {
					clearTimeout(loadingTimer);
					this.historyLoadingTimer = null;
				}
				const hideLoading = () => {
					if (this.historyLoadingToken === loadingToken) {
						this.historyLoadingVisible = false;
						this.historyLoadingVisibleAt = 0;
					}
				};
				const visibleElapsed = this.historyLoadingVisibleAt > 0
					? Date.now() - this.historyLoadingVisibleAt
					: HISTORY_LOADING_MIN_VISIBLE_MS;
				const delay = this.historyLoadingVisible
					? Math.max(HISTORY_LOADING_MIN_VISIBLE_MS - visibleElapsed, 0)
					: 0;
				if (delay > 0) {
					setTimeout(hideLoading, delay);
				} else {
					hideLoading();
				}
				if (this.suppressMessageGrowthScroll) {
					this.$nextTick(() => {
						this.suppressMessageGrowthScroll = false;
					});
				}
			});
		},
		updateFriendInfo() {
			if (this.isFriend) {
				let friend = {
					...this.friend
				};
				friend.headImage = this.userInfo.headImageThumb;
				friend.nickName = this.userInfo.nickName;
				friend.showNickName = friend.remarkNickName ? friend.remarkNickName : friend.nickName;
				this.friendStore.updateFriend(friend);
				this.groupStore.syncGroupMemberFromFriend(friend);
				this.chatStore.updateChatFromFriend(friend);
			} else {
				this.groupStore.syncGroupMemberFromUser(this.userInfo);
				this.chatStore.updateChatFromUser(this.userInfo);
			}
		},
		hydratePrivateUserInfo(friendId) {
			const friend = this.friendStore.findFriend(friendId);
			if (!friend || friend.deleted) {
				return false;
			}
			const currentUserInfo = this.userInfo && this.userInfo.id == friendId ? this.userInfo : {};
			this.userInfo = {
				...currentUserInfo,
				id: friend.id,
				userName: friend.userName || currentUserInfo.userName || '',
				nickName: friend.nickName || currentUserInfo.nickName || '',
					showNickName: friend.showNickName || currentUserInfo.showNickName || '',
					headImage: friend.headImage || currentUserInfo.headImage || currentUserInfo.headImageThumb || '',
					headImageThumb: friend.headImage || currentUserInfo.headImageThumb || currentUserInfo.headImage || '',
					online: typeof friend.online === 'boolean' ? friend.online : currentUserInfo.online,
				onlineWeb: typeof friend.onlineWeb === 'boolean' ? friend.onlineWeb : currentUserInfo.onlineWeb,
				onlineApp: typeof friend.onlineApp === 'boolean' ? friend.onlineApp : currentUserInfo.onlineApp,
				remarkNickName: friend.remarkNickName || currentUserInfo.remarkNickName || ''
			};
			return true;
		},
		loadFriend(friendId, options = {}) {
			const { forceRemote = false } = options;
			const hasLocalFriend = this.hydratePrivateUserInfo(friendId);
			if (hasLocalFriend && !forceRemote) {
				return Promise.resolve(this.userInfo);
			}
			return this.$http({
				url: `/user/find/${friendId}`,
				method: 'GET'
			}).then((userInfo) => {
				if (!this.chat || this.chat.type != 'PRIVATE' || this.chat.targetId != friendId) {
					return userInfo;
				}
				this.userInfo = {
					...this.userInfo,
					...userInfo
				};
				this.updateFriendInfo();
				return this.userInfo;
			});
		},
		showName(msgInfo) {
			if (!msgInfo) return "";
			if (this.isGroup) {
				let member = this.groupMemberMap[String(msgInfo.sendId)];
				return member ? member.showNickName : (msgInfo.sendNickName || "");
			} else if (msgInfo.sendId == this.mine.id) {
				return this.mine.nickName;
			} else {
				return this.chat.showName;
			}
		},
		headImage(msgInfo) {
			if (this.isGroup) {
				let member = this.groupMemberMap[String(msgInfo.sendId)];
				return member ? member.headImage : (msgInfo.sendHeadImage || "");
			} else {
				return msgInfo.selfSend ? this.mine.headImageThumb : this.chat.headImage
			}
		},
		resetEditor() {
			this.$nextTick(() => {
				const chatFooter = this.getChatFooterRef();
				if (!chatFooter) {
					return;
				}
				if (chatFooter.clearInput) {
					chatFooter.clearInput();
				}
				if (chatFooter.focusInput) {
					chatFooter.focusInput();
				}
			});
		},
		scrollToBottom() {
			this.resetShowMessages();
			this.$nextTick(() => {
				let div = this.$refs.messageViewport && this.$refs.messageViewport.getScrollElement
					? this.$refs.messageViewport.getScrollElement()
					: document.getElementById("chatScrollBox");
				if (!div) {
					return;
				}
				div.scrollTop = div.scrollHeight;
			});
		},
		refreshPlaceHolder() {
			if (this.isReceipt) {
				this.placeholder = "[回执消息]"
			} else if (this.$refs.editBox && this.$refs.editBox.innerHTML) {
				this.placeholder = ""
			} else {
				this.placeholder = "聊点什么吧~";
			}
		},
		sendMessageRequest(msgInfo, chatInfo = this.chat) {
			return new Promise((resolve, reject) => {
				const requestChat = chatInfo ? {
					type: chatInfo.type,
					targetId: chatInfo.targetId
				} : null;
				// 请求入队列，防止请求"后发先至"，导致消息错序
				this.reqQueue.push({ msgInfo, chatInfo: requestChat, resolve, reject });
				this.processReqQueue();
			})
		},
		processReqQueue() {
			if (this.reqQueue.length && !this.isSending) {
				this.isSending = true;
				const reqData = this.reqQueue.shift();
				const chatType = reqData.chatInfo && reqData.chatInfo.type
					? String(reqData.chatInfo.type).toLowerCase()
					: (reqData.msgInfo && reqData.msgInfo.groupId !== undefined ? 'group' : 'private');
				this.$http({
					url: `/message/${chatType}/send`,
					method: 'post',
					data: this.buildMessageSendPayload(reqData.msgInfo, reqData.chatInfo)
				}).then((res) => {
					reqData.resolve(res)
				}).catch((e) => {
					reqData.reject(e)
				}).finally(() => {
					this.isSending = false;
					// 发送下一条请求
					this.processReqQueue();
				})
			}
		},
		handleSendError(error, msgInfo, chat) {
			if (error && error.code === 40004) {
				this.forgetAttachmentRetry(msgInfo);
				this.chatStore.deleteMessage(msgInfo, chat);
				this.chatStore.refreshLastContent(chat);
				return;
			}
			msgInfo.status = this.$enums.MESSAGE_STATUS.FAILED;
			this.chatStore.updateMessage(msgInfo, chat);
		},
		buildTmpMessage(msgInfo) {
			let message = JSON.parse(JSON.stringify(msgInfo));
			message.sendId = this.mine.id;
			message.sendTime = new Date().getTime();
			message.status = this.$enums.MESSAGE_STATUS.SENDING;
			message.selfSend = true;
			if (this.isGroup) {
				message.readedCount = 0;
			}
			return message;
		},
		getImageSize(file) {
			return new Promise((resolve, reject) => {
				const reader = new FileReader();
				reader.onload = function (event) {
					const img = new Image();
					img.onload = function () {
						resolve({ width: img.width, height: img.height });
					};
					img.onerror = function () {
						reject(new Error('无法加载图片'));
					};
					img.src = event.target.result;
				};
				reader.onerror = function () {
					reject(new Error('无法读取文件'));
				};
				reader.readAsDataURL(file);
			});
		},
		getVideoPreviewInfo(file) {
			return new Promise((resolve) => {
				const url = URL.createObjectURL(file);
				const video = document.createElement('video');
				let settled = false;
				const cleanup = () => {
					video.onloadedmetadata = null;
					video.onseeked = null;
					video.onerror = null;
					video.removeAttribute('src');
					video.load();
					URL.revokeObjectURL(url);
				};
				const finish = (result) => {
					if (settled) {
						return;
					}
					settled = true;
					cleanup();
					resolve(result || { width: 0, height: 0, coverUrl: '' });
				};
				const capture = () => {
					const width = video.videoWidth || 0;
					const height = video.videoHeight || 0;
					if (!width || !height) {
						finish({ width: 0, height: 0, coverUrl: '' });
						return;
					}
					const canvas = document.createElement('canvas');
					const maxEdge = 720;
					const ratio = Math.min(1, maxEdge / Math.max(width, height));
					canvas.width = Math.max(1, Math.round(width * ratio));
					canvas.height = Math.max(1, Math.round(height * ratio));
					const context = canvas.getContext('2d');
					if (!context) {
						finish({ width, height, coverUrl: '' });
						return;
					}
					context.drawImage(video, 0, 0, canvas.width, canvas.height);
					canvas.toBlob((blob) => {
						if (!blob) {
							finish({ width, height, coverUrl: '' });
							return;
						}
						finish({ width, height, coverUrl: URL.createObjectURL(blob) });
					}, 'image/jpeg', 0.8);
				};
				video.preload = 'metadata';
				video.muted = true;
				video.playsInline = true;
				video.onloadedmetadata = () => {
					const seekTime = Math.min((Number(video.duration) || 0) / 3, 1);
					if (seekTime > 0) {
						try {
							video.currentTime = seekTime;
							return;
						} catch (error) {
							capture();
							return;
						}
					}
					capture();
				};
				video.onseeked = capture;
				video.onerror = () => {
					finish({ width: 0, height: 0, coverUrl: '' });
				};
				video.src = url;
			});
		},
		generateId() {
			// 生成临时id 
			const id = String(new Date().getTime()) + String(Math.floor(Math.random() * 1000));
			// 必须保证id是递增
			if (this.maxTmpId > id) {
				return this.generateId();
			}
			this.maxTmpId = id;
			return id;
		},
		sendStickerMessage(sticker) {
			if (!this.checkSendRateLimit()) {
				return;
			}
			const msgInfo = {
				tmpId: this.generateId(),
				content: JSON.stringify(sticker),
				type: this.$enums.MESSAGE_TYPE.STICKER,
				receipt: this.isReceipt
			};
			this.fillTargetId(msgInfo, this.chat.targetId);
			const chat = this.chat;
			const tmpMessage = this.buildTmpMessage(msgInfo);
			tmpMessage.content = msgInfo.content;
			tmpMessage.type = msgInfo.type;
			this.chatStore.insertMessage(tmpMessage, chat);
			this.sendMessageRequest(msgInfo, chat).then(m => {
				tmpMessage.id = m.id;
				tmpMessage.status = m.status;
				tmpMessage.content = m.content || tmpMessage.content;
				this.chatStore.updateMessage(tmpMessage, chat);
			}).catch((error) => {
				this.handleSendError(error, tmpMessage, chat);
			}).finally(() => {
				this.scrollToBottom();
				this.isReceipt = false;
				this.refreshPlaceHolder();
			})
		}
	},
	computed: {
		chatIsDnd() {
			return this.chatStore.isDnd(this.chat);
		},
		mine() {
			return this.userStore.userInfo;
		},
		activeGroupId() {
			return this.resolveCurrentGroupId();
		},
		uploadConfig() {
			return this.configStore.globalConfig?.upload || {};
		},
		chatImageMaxSize() {
			return this.resolveUploadSize(this.uploadConfig.imageMaxMb, 500);
		},
		chatFileMaxSize() {
			return this.resolveUploadSize(this.uploadConfig.fileMaxMb, 500);
		},
		chatVideoMaxSize() {
			return this.resolveUploadSize(this.uploadConfig.videoMaxMb, 500);
		},
		chatAudioMaxSize() {
			return this.resolveUploadSize(this.uploadConfig.audioMaxMb, 500);
		},
		isFriend() {
			return this.friendStore.isFriend(this.userInfo.id);
		},
		friend() {
			return this.friendStore.findFriend(this.userInfo.id) || {}
		},
		group() {
			return this.activeGroupId ? (this.groupStore.findGroup(this.activeGroupId) || {}) : {}
		},
		groupMembers() {
			return this.group.members || [];
		},
		groupMemberMap() {
			if (!this.isGroup) {
				return EMPTY_GROUP_MEMBER_LOOKUP;
			}
			const groupId = this.activeGroupId;
			if (!groupId) {
				return EMPTY_GROUP_MEMBER_LOOKUP;
			}
			const memberCount = Array.isArray(this.groupMembers) ? this.groupMembers.length : 0;
			const memberVersion = Number(this.group && this.group.memberPage && this.group.memberPage.loadedAt) || 0;
			return this.groupStore.getMemberLookup(groupId, memberCount, memberVersion) || EMPTY_GROUP_MEMBER_LOOKUP;
		},
		canAutoLoadOlderHistory() {
			return this.showMinIdx === 0 && !this.historyLoading &&
				!this.historyLoadingVisible && !this.historyError && !this.historyFinished &&
				this.messageList.some(message => message && message.type != this.$enums.MESSAGE_TYPE.TIP_TIME && message.id);
		},
		historyStatusType() {
			if (this.historyLoadingVisible) {
				return 'loading';
			}
			if (!this.isNearHistoryTop && this.messageList.length > 0) {
				return '';
			}
			if (this.historyError) {
				return 'error';
			}
			if (this.historyFinished) {
				return 'finished';
			}
			return '';
		},
		bottomWindowStatusType() {
			return this.bottomWindowLoadingVisible ? 'loading' : '';
		},
		latestTopMessage() {
			const topMessages = Array.isArray(this.group.topMessages) ? this.group.topMessages : [];
			if (!topMessages.length) {
				return null;
			}
			return topMessages[topMessages.length - 1];
		},
		topMessageCount() {
			const topMessages = Array.isArray(this.group.topMessages) ? this.group.topMessages : [];
			return topMessages.length;
		},
		canManageTopMessages() {
			return this.isGroup && (this.isOwner || this.isManager);
		},
		showHeaderTopBanner() {
			return this.isGroup && !!this.latestTopMessage;
		},
		showHeaderRtcBanner() {
			return this.isGroup && this.group.rtcInfo && this.group.rtcInfo.isChating;
		},
		showHeaderBannerStack() {
			return this.showHeaderTopBanner || this.showHeaderRtcBanner;
		},
		headerLayoutKey() {
			return `${this.showHeaderTopBanner ? 'top' : ''}:${this.showHeaderRtcBanner ? 'rtc' : ''}:${this.groupInfoError ? 'error' : ''}`;
		},
		headerMentionText() {
			if (this.chat.atMe) {
				return '@我待处理';
			}
			if (this.chat.atAll) {
				return '@全体提醒';
			}
			return '';
		},
		headerSubtitle() {
			if (this.isGroup) {
				const parts = [];
				if (this.memberCount > 0) {
					parts.push(`${this.memberCount} 人`);
				}
				if (this.canViewOnlineCount && this.onlineCount > 0) {
					parts.push(`${this.onlineCount} 在线`);
				}
				return parts.join(' · ');
			}
			if (this.isPrivate) {
				const parts = [];
				let online = null;
				if (typeof this.friend.online === 'boolean') {
					online = this.friend.online;
				} else if (typeof this.userInfo.online === 'boolean') {
					online = this.userInfo.online;
				}
				if (online === true) {
					parts.push('在线');
				}
				return parts.join(' · ');
			}
			return '';
		},
		title() {
			return this.chat.showName;
		},
		groupMemberStats() {
			const stats = {
				activeCount: 0,
				activeOnlineCount: 0
			};
			(this.groupMembers || []).forEach((member) => {
				if (!member || member.quit) {
					return;
				}
				stats.activeCount++;
				if (member.online) {
					stats.activeOnlineCount++;
				}
			});
			return stats;
		},
		memberCount() {
			if (Number.isFinite(Number(this.group.memberCount))) {
				return Number(this.group.memberCount);
			}
			return this.groupMemberStats.activeCount;
		},
		onlineCount() {
			const groupId = this.activeGroupId;
			const group = this.groupStore.findGroup(groupId);
			if (group && typeof group.onlineCount === 'number') {
				return group.onlineCount;
			}
			return this.groupMemberStats.activeOnlineCount;
		},
		onlinePermissionVersion() {
			const groupId = this.activeGroupId;
			if (!groupId) {
				return '';
			}
			const group = this.groupStore.findGroup(groupId);
			return group ? `${groupId}:${group.isAllowViewOnlineCount}:${this.isOwner}:${this.isManager}` : '';
		},
		canViewOnlineCount() {
			const groupId = this.activeGroupId;
			if (!this.isGroup || !groupId) {
				return false;
			}
			return this.$permission.hasGroupPermission(groupId, 'isAllowViewOnlineCount');
		},
		messageList() {
			if (!this.chat || !Array.isArray(this.chat.messages)) {
				return [];
			}
			return this.chat.messages;
		},
		unreadCount() {
			return this.chat.unreadCount;
		},
		showMessages() {
			const messages = this.messageList;
			if (this.showMaxIdx > 0) {
				return messages.slice(this.showMinIdx, this.showMaxIdx)
			} else {
				return messages.slice(this.showMinIdx)
			}
		},
		messageSize() {
			return this.messageList.length;
		},
		rawMessageSize() {
			if (!this.chat || !Array.isArray(this.chat.messages)) {
				return 0;
			}
			return this.chat.messages.length;
		},
		isGroup() {
			return this.chat.type == 'GROUP';
		},
		isPrivate() {
			return this.chat.type == 'PRIVATE';
		},
		isOwner() {
			return this.group.ownerId == this.mine.id;
		},
		isManager() {
			const groupId = this.activeGroupId;
			if (!groupId) {
				return false;
			}
			return this.groupStore.isManager(groupId, this.mine.id);
		},
		loading() {
			return this.chatStore.loading;
		},
			notAllowInputTip() {
				if (this.isGroup) {
					if (this.groupInfoError) {
						return "群资料暂时不可用，请重试"
					} else if (this.group.dissolve) {
					return "群聊已解散"
				} else if (this.group.quit) {
					return "您已不在群聊中"
				} else if (this.group.isBanned) {
					let tip = `群聊已被封禁,原因:${this.group.reason}`;
					if (this.group.unbanTime) {
						tip += `,恢复时间: ${this.group.unbanTime}`;
					}
					return tip;
				} else if (this.group.isAllMuted && !this.isOwner && !this.isManager) {
					return "全员禁言中，只有群主和管理员发言"
				} else if (this.group.isMuted) {
					return "您已被群管理员禁言"
				}
			} else if (this.userInfo.isBanned) {
				let tip = `对方账号已被封禁,原因:${this.userInfo.reason}`;
				if (this.userInfo.unbanTime) {
					tip += `,恢复时间: ${this.userInfo.unbanTime}`;
				}
				return tip;
			}
			return "";
		}
	},
	mounted() {
		this.disposeDismiss = dismissStack.register({
			id: `chat-box-side-${this._uid}`,
			isOpen: () => this.showSide,
			getZIndex: () => this.resolveDismissZIndex(),
			close: () => {
				this.showSide = false;
			}
		});
		this.$nextTick(() => this.observeHeaderLayout());
	},
	beforeDestroy() {
		this.groupInfoRequestToken++;
		if (this.headerResizeObserver) {
			this.headerResizeObserver.disconnect();
			this.headerResizeObserver = null;
		}
		if (this.headerLayoutSnapshotFrame !== null) {
			if (typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function') {
				window.cancelAnimationFrame(this.headerLayoutSnapshotFrame);
			}
			clearTimeout(this.headerLayoutSnapshotFrame);
			this.headerLayoutSnapshotFrame = null;
		}
		if (this.historyLoadingTimer) {
			clearTimeout(this.historyLoadingTimer);
			this.historyLoadingTimer = null;
		}
		if (this.disposeDismiss) {
			this.disposeDismiss();
			this.disposeDismiss = null;
		}
	},
	watch: {
		headerLayoutKey() {
			const snapshot = this.captureHeaderLayoutSnapshot();
			if (snapshot) {
				this.headerLayoutSnapshot = snapshot;
			}
			this.queueHeaderLayoutCompensation();
		},
		chat: {
			handler(newChat, oldChat) {
				if (newChat.targetId > 0 && (!oldChat || newChat.type != oldChat.type ||
					newChat.targetId != oldChat.targetId)) {
					this.normalizeChatMessages(newChat);
					this.userInfo = {};
					this.groupId = null;
					this.groupInfoRequestToken++;
					this.groupInfoLoading = false;
					this.groupInfoError = false;
					this.groupInfoErrorMessage = '';
					this.headerLayoutSnapshot = null;
					this.historyChatKey = this.resolveHistoryChatKey(newChat);
					this.historyLoadingToken++;
					if (this.historyLoadingTimer) {
						clearTimeout(this.historyLoadingTimer);
						this.historyLoadingTimer = null;
					}
					this.historyLoading = false;
					this.historyLoadingVisible = false;
					this.historyLoadingVisibleAt = 0;
					this.historyError = false;
					this.historyFinished = false;
					this.historyCursor = '';
					this.bottomWindowLoadingVisible = false;
					this.bottomWindowLoadingToken = 0;
					this.bottomWindowExpanding = false;
					this.isNearHistoryTop = false;
					if (this.isGroup) {
						const activeGroupId = newChat.targetId;
						this.groupId = activeGroupId;
						if (this.consumeDeferredInitialGroupSync(newChat)) {
							this.loadNewGroupAfterFirstPaint(activeGroupId);
						} else {
							this.loadGroup(activeGroupId, {
								refreshMembers: false,
								refreshOnline: false
							});
						}
						this.warmupGroupMembersAfterPaint(activeGroupId, {
							refreshOnline: true
						});
					} else {
						this.loadFriend(this.chat.targetId, {
							forceRemote: false
						});
						this.loadChatHistory(newChat);
						// 加载已读状态
						this.loadReaded(this.chat.targetId)
					}
					// 滚到底部
					this.scrollToBottom();
					this.showSide = false;
					// 消息已读
					this.readedMessage()
					// 复位回执消息
					this.isReceipt = false;
					// 清空引用消息
					this.quoteMessage = null;
					// 复位底部标记
					this.isInBottom = true;
					this.newMessageSize = 0;
					// 清空消息临时id
					this.maxTmpId = 0;
					this.topMessageSyncing = false;
					// 退出多选模式
					this.exitMultiSelectMode();
					// 更新placeholder
					this.refreshPlaceHolder();
					// 先保存旧会话草稿，再恢复新会话草稿（避免 nextTick 后输入框已被清空导致保存为空）
					const chatFooter = this.getChatFooterRef();
					if (chatFooter && chatFooter.saveDraft) {
						chatFooter.saveDraft(oldChat);
					}
					this.$nextTick(() => {
						this.headerLayoutSnapshot = this.captureHeaderLayoutSnapshot();
						const nextChatFooter = this.getChatFooterRef();
						if (nextChatFooter && nextChatFooter.restoreDraft) {
							nextChatFooter.restoreDraft(newChat);
						}
					})
				}
			},
			immediate: true
		},
		messageSize: {
			handler(newSize, oldSize) {
				if (this.suppressMessageGrowthScroll) {
					return;
				}
				if (newSize > oldSize) {
					this.trimMessageWindowFromTop();
					// 收到普通消息,则滚动至底部
					let lastMessage = this.messageList[newSize - 1];
					if (lastMessage && (this.$msgType.isNormal(lastMessage.type) || this.$msgType.isAction(lastMessage.type))) {
						if (this.isInBottom || lastMessage.selfSend) {
							this.scrollToBottom();
						} else {
							this.newMessageSize++;
						}
					}
				}
			}
		},
		loading: {
			handler(newLoading, oldLoading) {
				// 断线重连后，需要更新一下已读状态
				if (!newLoading && this.isPrivate) {
					this.loadReaded(this.chat.targetId)
				}
			}
		},
		onlinePermissionVersion() {
			const groupId = this.activeGroupId;
			if (groupId) {
				this.refreshGroupOnlineCount(groupId);
			}
		}
	},
}
</script>

<style lang="scss">
.chat-box {
	position: relative;
	width: 100%;
	height: 100%;
	min-height: 0;
	display: flex;
	flex-direction: column;

	.chat-container {
		position: relative;
		flex: 1;
		display: flex;
		flex-direction: row;
		overflow: hidden;
		min-height: 0;
	}

	.chat-main-area {
		flex: 1;
		display: flex;
		flex-direction: column;
		overflow: hidden;
		min-height: 0;
		min-width: 0;
	}

	.chat-header-stack {
		--chat-header-inline-padding: 12px;
		position: relative;
		z-index: 30;
		display: flex;
		flex-direction: column;
		flex-shrink: 0;
		border-bottom: 1px solid var(--im-surface-panel-border);
		background: var(--im-chat-bg);
	}

	.chat-header-banner-stack {
		position: relative;
		display: flex;
		flex-direction: column;
		flex-shrink: 0;
		gap: 0;
		width: 100%;
		box-sizing: border-box;
		border-top: 1px solid var(--im-surface-panel-border);
		background: var(--im-chat-bg);
		box-shadow: 0 12px 24px -22px rgba(var(--im-black-rgb), 0.38);

		> * {
			min-width: 0;
			padding-top: 4px;
		}

		> * + * {
			border-top: 1px solid var(--im-surface-panel-border);
		}
	}

	.group-info-error {
		display: flex;
		align-items: center;
		gap: 8px;
		min-height: 36px;
		padding: 4px 12px;
		box-sizing: border-box;
		border-top: 1px solid rgba(var(--im-warning-rgb), 0.22);
		background: rgba(var(--im-warning-rgb), 0.08);
		color: var(--im-color-warning);
		font-size: 12px;
		line-height: 1.4;

		span {
			flex: 1;
			min-width: 0;
		}

		.el-button {
			flex: 0 0 auto;
			padding: 4px;
		}
	}

	.chat-body {
		flex: 1;
		display: flex;
		overflow: hidden;
		min-height: 0;
	}

	.content-box {
		flex: 1;
		display: flex;
		flex-direction: column;
		overflow: hidden;
		min-height: 0;
		position: relative;

		.message-stage {
			flex: 1;
			min-height: 0;
			position: relative;
			display: flex;
			flex-direction: column;
		}

		.locate-tip {
			position: absolute;
			right: 12px;
			bottom: 14px;
			color: var(--im-text-color);
			font-size: 12px;
			font-weight: 600;
			background: var(--im-surface-panel-strong);
			cursor: pointer;
			z-index: 99;
			box-shadow: 0 18px 28px -24px rgba(var(--im-black-rgb), 0.4);
			transition: all 0.3s ease;
			border: 1px solid var(--im-surface-panel-border);
			backdrop-filter: blur(14px);

			i {
				font-size: 14px;
				color: var(--im-color-primary);
			}

			.tip-label {
				white-space: nowrap;
			}

			.tip-badge {
				display: inline-flex;
				align-items: center;
				justify-content: center;
				min-width: 24px;
				height: 24px;
				padding: 0 7px;
				border-radius: 999px;
				background: var(--im-color-primary);
				color: rgba(var(--im-white-rgb), 1);
				font-size: 12px;
				line-height: 1;
				box-shadow: 0 12px 18px -16px rgba(var(--im-primary-rgb), 0.9);
			}

			&.scroll-tip {
				display: inline-flex;
				align-items: center;
				justify-content: center;
				width: 38px;
				height: 38px;
				padding: 0;
				border-radius: 50%;

				i {
					font-size: 16px;
				}

				.tip-badge {
					position: absolute;
					top: -4px;
					right: -3px;
					min-width: 22px;
					height: 22px;
					padding: 0 6px;
					font-size: 11px;
				}
			}

			&.mention-tip {
				display: inline-flex;
				align-items: center;
				gap: 6px;
				min-height: 34px;
				padding: 0 10px 0 7px;
				border-radius: 999px;
				max-width: min(220px, calc(100% - 32px));
				color: var(--im-color-primary);

				.tip-badge {
					background: rgba(var(--im-primary-rgb), 0.12);
					color: var(--im-color-primary);
				}
			}

			&:hover {
				transform: translateY(-1px);
				box-shadow: 0 10px 18px rgba(var(--im-black-rgb), 0.12);
			}
		}

	}

	.chat-side-box {
		width: 0;
		flex-shrink: 0;
		overflow: hidden;
		border-left: none;
		background: var(--im-chat-bg);
		transition: width 0.25s ease, border-left 0.1s ease;

		&.side-open {
			width: clamp(320px, 25vw, 360px);
			overflow-y: auto;
			border-left: 1px solid var(--im-surface-panel-border);
		}
	}
}

@media (max-width: 767px) {
	.chat-box {
	}
}

.chat-mobile-side-overlay {
	position: fixed;
	inset: 0;
	z-index: 260;
}

.chat-mobile-side-mask {
	position: absolute;
	inset: 0;
	border: none;
	padding: 0;
	background: rgba(var(--im-black-rgb), 0.22);
	backdrop-filter: blur(2px);
	cursor: pointer;
}

.chat-mobile-side-drawer {
	position: absolute;
	top: 0;
	right: 0;
	bottom: 0;
	width: min(88vw, 320px);
	max-width: calc(100vw - 20px);
	padding-top: env(safe-area-inset-top, 0px);
	padding-bottom: env(safe-area-inset-bottom, 0px);
	box-sizing: border-box;
	background: var(--im-chat-bg);
	border-left: 1px solid var(--im-surface-panel-border);
	box-shadow: -14px 0 32px -26px rgba(var(--im-black-rgb), 0.38);
	overflow: hidden;
}

</style>

<style lang="scss">
.chat-box .chat-header-stack .chat-header .action-bar {
	gap: 7px;
}

.chat-box .chat-header-stack .chat-header .action-btn,
.chat-box .chat-header-stack .chat-header .btn-side,
.chat-box .chat-header-stack .chat-header .nav-back {
	width: 36px;
	height: 36px;
	border-radius: 10px;
}

.chat-box .chat-header-stack .chat-header .action-btn:hover,
.chat-box .chat-header-stack .chat-header .btn-side:hover,
.chat-box .chat-header-stack .chat-header .nav-back:hover {
	border-color: rgba(var(--im-primary-rgb), 0.16);
	box-shadow: 0 0 0 1px rgba(var(--im-primary-rgb), 0.04);
}

.chat-box .chat-header-stack .chat-header .action-btn:active,
.chat-box .chat-header-stack .chat-header .btn-side:active,
.chat-box .chat-header-stack .chat-header .nav-back:active {
	box-shadow: inset 0 0 0 1px rgba(var(--im-primary-rgb), 0.04);
}

.chat-box .chat-header-stack .chat-header .action-btn__glyph {
	font-size: 16px;
}

.chat-box .chat-header-stack .chat-header .action-btn__glyph.el-icon-chat-dot-round {
	font-size: 16px;
}

.chat-box .chat-header-stack .chat-header .action-btn__glyph.el-icon-more {
	font-size: 16px;
}
</style>
